/**
 * AssistantOverlay - Professional overlay engine
 * 
 * Creates a fluid slide-in panel with tabbed interface:
 * - Context: Page info and extracted entities  
 * - Chat: Integrated A2UIChat from @merlin/chat
 * - Explorer: Integrated MerlinGraph from @merlin/graph
 */

import { browserAPI, isExtension, getResourceURL, storage } from '@merlin/lib/js/browser-api';
import { getTheme, setTheme, onThemeChange } from '@merlin/lib/js/theme';
import { captureError, addBreadcrumb, setUser, setExtra } from '../../../lib/js/sentry.js';
import { safeSetInnerHTML, safeInsertAdjacentHTML } from './trusted-types.js';
import '@merlin/lib/styles/index.css';

// Import A2UIChat from the chat extension
// This provides the full chat UI with message history, streaming, and task tracking
import { A2UIChat } from '@merlin/chat';

// Import MerlinGraph from the graph extension
// MerlinGraph wraps GraphExplorerMinimal and initializes all UI modules (toolbar, tabs, windows, panels)
import { MerlinGraph } from '@merlin/graph';

// Import graph explorer styles (MerlinGraph handles loading these but we import for bundling)
import '@merlin/graph-styles/base.css';
import '@merlin/graph-styles/toolbar.css';
import '@merlin/graph-styles/panels.css';
import '@merlin/graph-styles/tabs.css';

// Load Lucide icons from CDN if not available
async function loadLucide() {
    if (typeof lucide !== 'undefined') return;

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
        script.onload = () => {
            if (typeof lucide !== 'undefined') {
                lucide.createIcons();
            }
            resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

export class AssistantOverlay {
    constructor(options = {}) {
        // Accept both api_token and access_token for backwards compatibility
        const accessToken = options.access_token || options.api_token || null;
        
        this.options = {
            theme: 'light',
            position: 'right',
            width: '40%',
            min_width: '440px',
            show_context: true,
            show_chat: true,
            show_explorer: true,
            show_artefacts: true,
            api_endpoint: null,
            workspace_id: null,
            canvas_id: null,
            layout_mode: 'compact',  // 'compact' or 'full'
            ...options,
            // Ensure access_token is always set from our resolved value
            access_token: accessToken,
        };

        this.container = null;
        this.isOpen = false;
        this.isExpanded = false;
        this.activeTab = 'context';
        this.pageContext = {};
        this.cursorId = null;
        this.canvasId = options.canvas_id || null;
        this.selectedPersonaId = null;
        this.selectedCanvasId = null;
        this.selectedCursorId = null;
        this.selectedWorkspaceId = null;

        // Sub-components (lazy loaded)
        this.chatPanel = null;
        this.graphExplorer = null;
        this.artefactsPanel = null;

        // Artefacts data
        this.artefacts = [];

        // Track initialization state
        this._initialized = false;
        this._initPromise = this._init();
    }

    async _resolveWorkspaceId() {
        // 1. Check for FAXIOM__WORKSPACE (global variable or process.env)
        const envWorkspaceId = (typeof process !== 'undefined' && process.env && process.env.FAXIOM__WORKSPACE) ||
            (typeof window !== 'undefined' && window.FAXIOM__WORKSPACE);

        if (envWorkspaceId) {
            return envWorkspaceId;
        }

        // 2. Check localStorage for previously selected workspace
        try {
            const savedWorkspaceId = localStorage.getItem('assistant_workspace_id');
            if (savedWorkspaceId) {
                return savedWorkspaceId;
            }
        } catch (e) {
            console.warn('[AssistantOverlay] localStorage read failed:', e);
        }

        // 3. Check extension storage for workspace.id
        try {
            const data = await storage.get(['workspace.id', 'workspace']);
            return data['workspace.id'] || data.workspace?.id || null;
        } catch (error) {
            console.warn('[AssistantOverlay] Storage read failed:', error);
            return null;
        }
    }

    /**
     * Resolve API endpoint from various sources
     * Priority: 1. Explicit option (including empty string for proxy/relative paths), 
     *           2. Global variable, 3. Storage, 4. Current page origin
     */
    async _resolveApiEndpoint() {
        // 1. Use explicit option if provided (empty string means use relative paths for proxy)
        if (this.options.api_endpoint !== undefined && this.options.api_endpoint !== null) {
            return this.options.api_endpoint;  // Returns empty string if that's what was passed
        }

        // 2. Check for FAXIOM API endpoint global variable (both underscore styles)
        const envEndpoint = (typeof process !== 'undefined' && process.env && (process.env.FAXIOM__API_ENDPOINT || process.env.FAXIOM_API_ENDPOINT)) ||
            (typeof window !== 'undefined' && (window.FAXIOM__API_ENDPOINT || window.FAXIOM_API_ENDPOINT));
        if (envEndpoint) {
            return envEndpoint;
        }

        // 3. Check storage for api_endpoint
        try {
            const data = await storage.get(['api_endpoint', 'apiEndpoint']);
            if (data.api_endpoint || data.apiEndpoint) {
                return data.api_endpoint || data.apiEndpoint;
            }
        } catch (error) {
            console.warn('[AssistantOverlay] Storage read for api_endpoint failed:', error);
        }

        // 4. Try to detect from current page origin (for Dash app context)
        if (typeof window !== 'undefined' && window.location) {
            const origin = window.location.origin;
            // If we're on a Faxiom/Merlin domain, use the current origin
            if (origin && (origin.includes('faxiom') || origin.includes('merlin') || origin.includes('localhost'))) {
                return origin;
            }
        }

        return null;
    }

    /**
     * Get authentication token from session
     * Checks sessionStorage for user token (Dash app context)
     */
    _getSessionAuth() {
        try {
            // Check Dash's 'user' store from sessionStorage
            const userStoreData = sessionStorage.getItem('user');
            if (userStoreData) {
                const user = JSON.parse(userStoreData);
                if (user && user.token) {
                    return { token: user.token, method: 'bearer' };
                }
            }
        } catch (e) {
            console.warn('[AssistantOverlay] Failed to parse user from session:', e);
        }

        // Check for stored API key
        const apiKey = sessionStorage.getItem('explorerApiKey');
        if (apiKey) {
            return { apiKey, method: 'apikey' };
        }

        // Fall back to access_token option
        if (this.options.access_token) {
            return { token: this.options.access_token, method: 'bearer' };
        }

        return null;
    }
    
    /**
     * Check if api_endpoint is configured (empty string is valid for proxy mode)
     */
    _hasApiEndpoint() {
        return this.options.api_endpoint !== null && this.options.api_endpoint !== undefined;
    }

    async _init() {
        // Resolve API endpoint if not explicitly provided
        // Note: empty string is valid (means use relative paths for proxy)
        if (this.options.api_endpoint === undefined || this.options.api_endpoint === null) {
            this.options.api_endpoint = await this._resolveApiEndpoint();
        }
        
        // Resolve access token if not explicitly provided
        if (!this.options.access_token) {
            this.options.access_token = await this._checkAuthentication();
        }
        
        // Resolve workspace ID if not explicitly provided
        if (!this.options.workspace_id) {
            this.options.workspace_id = await this._resolveWorkspaceId();
        }
        
        // Also sync selectedWorkspaceId if we resolved a workspace
        if (this.options.workspace_id) {
            this.selectedWorkspaceId = this.options.workspace_id;
        }
        
        console.log('[AssistantOverlay] Initialized with:', {
            api_endpoint: this.options.api_endpoint,
            workspace_id: this.options.workspace_id,
            hasToken: !!this.options.access_token
        });

        // Create DOM first (synchronously) before any async operations
        this._createDOM();
        this._setupEventHandlers();
        this.setTheme(this.options.theme);

        // Load icons asynchronously after DOM is ready
        await loadLucide();

        // Re-render icons now that lucide is loaded
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }

        // Listen for theme changes
        onThemeChange((theme) => {
            this.options.theme = theme;
            this._updateTheme();
        });

        this._initialized = true;
    }

    // Wait for initialization to complete
    async ready() {
        await this._initPromise;
        return this;
    }

    _createDOM() {
        // Prevent duplicate overlays - remove existing if present
        const existingOverlay = document.getElementById('merlin-assistant-overlay');
        if (existingOverlay) {
            console.warn('[AssistantOverlay] Removing existing overlay to prevent duplication');
            existingOverlay.remove();
        }
        
        this.container = document.createElement('div');
        this.container.id = 'merlin-assistant-overlay';
        this.container.className = 'merlin-assistant-overlay merlin-extension';
        this.container.setAttribute('data-theme', this.options.theme);

        if (!this.options.workspace_id) {
            console.warn('[AssistantOverlay] No workspace_id provided - some features may be limited');
        }
        safeSetInnerHTML(this.container, this._getOverlayHTML());

        // Add styles
        this._injectStyles();

        document.body.appendChild(this.container);

        // Initialize Lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }
    }

    _getOverlayHTML() {
        const tabs = [];
        if (this.options.show_context) tabs.push({ id: 'context', icon: 'info', label: 'Context' });
        if (this.options.show_chat) tabs.push({ id: 'chat', icon: 'message-circle', label: 'Chat' });
        if (this.options.show_explorer) tabs.push({ id: 'explorer', icon: 'git-branch', label: 'Explorer' });
        if (this.options.show_artefacts) tabs.push({ id: 'artefacts', icon: 'file-box', label: 'Artefacts' });

        return `
            <div class="assistant-panel">
                <div class="assistant-header">
                    <div class="assistant-title">
                        <div class="assistant-logo">
                            <img src="data:image/png;base64,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" 
                                 alt="Assistant" 
                                 style="width: 24px; height: 24px; object-fit: contain;">
                        </div>
                        <span>Faxiom Assistant</span>
                    </div>
                    <div class="assistant-persona-selector">
                        <button class="assistant-persona-btn" id="assistant-persona-btn" title="Select persona">
                            <i data-lucide="user-circle"></i>
                            <span id="assistant-persona-name">Me</span>
                            <i data-lucide="chevron-down" class="persona-chevron"></i>
                        </button>
                        <div class="assistant-persona-dropdown" id="assistant-persona-dropdown">
                            <div class="persona-dropdown-header">Your Persona</div>
                            <div class="persona-dropdown-list" id="assistant-persona-list">
                                <div class="persona-loading">Loading...</div>
                            </div>
                        </div>
                    </div>
                    <div class="assistant-actions">
                        <button class="assistant-btn-icon" id="assistant-expand-btn" title="Expand panel">
                            <i data-lucide="maximize-2"></i>
                        </button>
                        <button class="assistant-btn-icon" id="assistant-refresh-btn" title="Refresh">
                            <i data-lucide="refresh-cw"></i>
                        </button>
                        <button class="assistant-btn-icon" id="assistant-theme-btn" title="Toggle theme">
                            <i data-lucide="${this.options.theme === 'light' ? 'moon' : 'sun'}"></i>
                        </button>
                        <button class="assistant-btn-icon" id="assistant-close-btn" title="Close">
                            <i data-lucide="x"></i>
                        </button>
                    </div>
                </div>
                
                <div class="assistant-tabs">
                    ${tabs.map(tab => `
                        <button class="assistant-tab ${tab.id === this.activeTab ? 'active' : ''}" 
                                data-tab="${tab.id}" title="${tab.label}">
                            <i data-lucide="${tab.icon}"></i>
                            <span class="assistant-tab-label">${tab.label}</span>
                        </button>
                    `).join('')}
                </div>
                
                <div class="assistant-content">
                    ${this.options.show_context ? this._getContextTabHTML() : ''}
                    ${this.options.show_chat ? this._getChatTabHTML() : ''}
                    ${this.options.show_explorer ? this._getExplorerTabHTML() : ''}
                    ${this.options.show_artefacts ? this._getArtefactsTabHTML() : ''}
                </div>
            </div>
        `;
    }

    _getContextTabHTML() {
        // Check if workspace is pre-configured via env var
        const hasEnvWorkspace = (typeof process !== 'undefined' && process.env && process.env.FAXIOM__WORKSPACE) ||
            (typeof window !== 'undefined' && window.FAXIOM__WORKSPACE);
        
        return `
            <div class="assistant-tab-content ${this.activeTab === 'context' ? 'active' : ''}" data-tab-content="context">
                <div class="context-panel">
                    <!-- Row 1: Workspace + Canvas (side-by-side in expanded) -->
                    <div class="context-row context-row-1">
                        <!-- Workspace Selection (only if not pre-configured) -->
                        ${!hasEnvWorkspace ? `
                        <div class="context-section context-section-workspace">
                            <div class="context-section-header">
                                <i data-lucide="home"></i>
                                <h4>Workspace</h4>
                            </div>
                            <div class="context-selector-card">
                                <select id="ctx-workspace-select" class="assistant-select">
                                    <option value="">Select workspace...</option>
                                </select>
                            </div>
                        </div>
                        ` : ''}
                        
                        <!-- Canvas Selection (in row 1 for expanded mode) -->
                        <div class="context-section context-section-canvas context-section-canvas-row1">
                            <div class="context-section-header">
                                <i data-lucide="layout-grid"></i>
                                <h4>Canvas</h4>
                            </div>
                            <div class="context-selector-card">
                                <select id="ctx-canvas-select" class="assistant-select">
                                    <option value="">Select a canvas...</option>
                                </select>
                                <small class="context-selector-hint">Scope agents to a specific canvas</small>
                            </div>
                        </div>
                    </div>
                    
                    <div class="context-separator"></div>
                    
                    <!-- Row 2: Page Info + Entities (side-by-side in expanded) -->
                    <div class="context-row context-row-2">
                        <!-- Page Information -->
                        <div class="context-section context-section-page">
                            <div class="context-section-header">
                                <i data-lucide="file-text"></i>
                                <h4>Page Information</h4>
                            </div>
                            <div class="context-info-card">
                                <div class="context-info-item">
                                    <span class="context-info-label">Title</span>
                                    <span class="context-info-value" id="ctx-page-title">Loading...</span>
                                </div>
                                <div class="context-info-divider"></div>
                                <div class="context-info-item">
                                    <span class="context-info-label">URL</span>
                                    <span class="context-info-value context-info-url" id="ctx-page-url">Loading...</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Detected Entities + Extract Button -->
                        <div class="context-section context-section-entities-group">
                            <div class="context-section context-section-entities">
                                <div class="context-section-header">
                                    <i data-lucide="tags"></i>
                                    <h4>Detected Entities</h4>
                                </div>
                                <div class="context-entities" id="ctx-entities">
                                    <div class="context-empty-state">
                                        <i data-lucide="sparkles"></i>
                                        <p>Extract entities from the current page</p>
                                    </div>
                                </div>
                            </div>
                            <button class="assistant-btn assistant-btn-secondary context-btn-extract" id="ctx-extract-btn">
                                <i data-lucide="scan"></i>
                                <span>Extract Entities</span>
                            </button>
                        </div>
                    </div>
                    
                    <div class="context-separator"></div>
                    
                    <!-- Row 3: Cursor + Add to Cursor (side-by-side in expanded) -->
                    <div class="context-row context-row-3">
                        <!-- Cursor Section with Stats -->
                        <div class="context-section context-section-cursor-group">
                            <!-- Cursor Selection -->
                            <div class="context-section context-section-cursor">
                                <div class="context-section-header">
                                    <i data-lucide="mouse-pointer-2"></i>
                                    <h4>Cursor</h4>
                                </div>
                                <div class="context-selector-card">
                                    <select id="ctx-cursor-select" class="assistant-select" disabled>
                                        <option value="">Select a canvas first</option>
                                    </select>
                                    <small class="context-selector-hint">Load saved cursor state</small>
                                </div>
                            </div>
                            <!-- Cursor Stats (placeholder) -->
                            <div class="context-cursor-stats" id="ctx-cursor-stats">
                                <div class="cursor-stats-placeholder">
                                    <span class="cursor-stat-item"><i data-lucide="database"></i> <span id="cursor-stat-nodes">-</span> nodes</span>
                                    <span class="cursor-stat-item"><i data-lucide="link"></i> <span id="cursor-stat-edges">-</span> edges</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Add to Cursor Button -->
                        <div class="context-section context-section-cursor-action">
                            <button class="assistant-btn assistant-btn-blue context-btn-cursor" id="ctx-add-cursor-btn">
                                <i data-lucide="mouse-pointer-2"></i>
                                <span>Add to Cursor</span>
                            </button>
                        </div>
                    </div>
                    
                    <div class="context-separator"></div>
                    
                    <!-- Row 4: Add to Graph Button -->
                    <div class="context-row context-row-4">
                        <button class="assistant-btn assistant-btn-green context-btn-graph" id="ctx-add-graph-btn">
                            <i data-lucide="git-branch"></i>
                            <span>Add to Graph</span>
                        </button>
                    </div>
                    
                    <!-- Graph Selection Modal -->
                    <div class="assistant-modal" id="graph-selection-modal" style="display: none;">
                        <div class="assistant-modal-backdrop"></div>
                        <div class="assistant-modal-content">
                            <div class="assistant-modal-header">
                                <h3>Select Stream</h3>
                                <button class="assistant-btn-icon" id="modal-close-btn">
                                    <i data-lucide="x"></i>
                                </button>
                            </div>
                            <div class="assistant-modal-body">
                                <div class="form-group">
                                    <label for="stream-select">Stream</label>
                                    <select id="stream-select" class="assistant-select">
                                        <option value="">Loading streams...</option>
                                    </select>
                                </div>
                            </div>
                            <div class="assistant-modal-footer">
                                <button class="assistant-btn assistant-btn-secondary" id="modal-cancel-btn">Cancel</button>
                                <button class="assistant-btn assistant-btn-green" id="modal-confirm-btn">Add to Graph</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    _getChatTabHTML() {
        return `
            <div class="assistant-tab-content ${this.activeTab === 'chat' ? 'active' : ''}" data-tab-content="chat">
                <div id="assistant-chat-container" class="assistant-chat-container">
                    <div class="chat-loading">
                        <div class="chat-loading-spinner"></div>
                        <p>Initializing chat...</p>
                    </div>
                </div>
            </div>
        `;
    }

    _getExplorerTabHTML() {
        return `
            <div class="assistant-tab-content ${this.activeTab === 'explorer' ? 'active' : ''}" data-tab-content="explorer">
                <div id="assistant-graph-container" class="assistant-graph-container">
                    <div class="graph-loading">
                        <div class="graph-loading-spinner"></div>
                        <p>Loading graph explorer...</p>
                    </div>
                </div>
            </div>
        `;
    }

    _getArtefactsTabHTML() {
        return `
            <div class="assistant-tab-content ${this.activeTab === 'artefacts' ? 'active' : ''}" data-tab-content="artefacts">
                <div id="assistant-artefacts-container" class="assistant-artefacts-container">
                    <div class="artefacts-loading">
                        <div class="artefacts-loading-spinner"></div>
                        <p>Loading artefacts...</p>
                    </div>
                </div>
            </div>
        `;
    }

    _injectStyles() {
        if (document.getElementById('merlin-assistant-styles')) return;

        const style = document.createElement('style');
        style.id = 'merlin-assistant-styles';
        style.textContent = `
            /* ========================================
               CSS Custom Properties (Design Tokens)
               ======================================== */
            .merlin-assistant-overlay {
                --ma-primary: #3B82F6;
                --ma-primary-hover: #60a5fa;
                --ma-primary-light: rgba(59, 130, 246, 0.15);
                --ma-success: #10B981;
                --ma-warning: #F59E0B;
                --ma-error: #EF4444;
                
                /* Dark theme (default) - matches main UI */
                --ma-bg: #0a0a0a;
                --ma-bg-secondary: #141414;
                --ma-bg-tertiary: #1f1f1f;
                --ma-surface: #141414;
                --ma-surface-hover: #1f1f1f;
                --ma-border: #262626;
                --ma-border-light: #333333;
                --ma-text: #fafafa;
                --ma-text-secondary: #a1a1aa;
                --ma-text-muted: #71717a;
                --ma-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.4);
                --ma-shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.2);
                
                /* Typography */
                --ma-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
                --ma-font-mono: 'JetBrains Mono', 'Fira Code', monospace;
                
                /* Spacing */
                --ma-radius: 12px;
                --ma-radius-sm: 8px;
                --ma-radius-xs: 6px;
                
                /* Transitions */
                --ma-transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                --ma-transition-slow: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .merlin-assistant-overlay[data-theme="light"] {
                --ma-primary: #1a1a1a;
                --ma-primary-hover: #333333;
                --ma-primary-light: rgba(0, 0, 0, 0.06);
                --ma-bg: #ffffff;
                --ma-bg-secondary: #fafafa;
                --ma-bg-tertiary: #f5f5f5;
                --ma-surface: #ffffff;
                --ma-surface-hover: #fafafa;
                --ma-border: #e5e5e5;
                --ma-border-light: #f0f0f0;
                --ma-text: #0a0a0a;
                --ma-text-secondary: #525252;
                --ma-text-muted: #a3a3a3;
                --ma-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.08), 0 8px 10px -6px rgb(0 0 0 / 0.06);
                --ma-shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04);
            }
            
            /* ========================================
               Base Overlay Styles
               ======================================== */
            .merlin-assistant-overlay {
                position: fixed;
                top: 0;
                ${this.options.position}: 0;
                height: 100vh;
                z-index: 2147483647;
                pointer-events: none;
                font-family: var(--ma-font);
                font-size: 14px;
                line-height: 1.5;
                color: var(--ma-text);
            }
            
            .merlin-assistant-overlay * {
                box-sizing: border-box;
            }
            
            /* Panel */
            .assistant-panel {
                position: absolute;
                top: 16px;
                ${this.options.position}: 16px;
                bottom: 16px;
                width: ${this.options.width};
                min-width: ${this.options.min_width || '440px'};
                max-width: calc(100vw - 32px);
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius);
                box-shadow: var(--ma-shadow);
                display: flex;
                flex-direction: column;
                overflow: hidden;
                pointer-events: auto;
                transform: translateX(${this.options.position === 'right' ? 'calc(100% + 32px)' : 'calc(-100% - 32px)'});
                opacity: 0;
                transition: var(--ma-transition-slow);
            }
            
            .merlin-assistant-overlay.open .assistant-panel {
                transform: translateX(0);
                opacity: 1;
            }
            
            /* ========================================
               Header
               ======================================== */
            .assistant-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 20px;
                border-bottom: 1px solid var(--ma-border);
                background: var(--ma-bg);
            }
            
            .assistant-title {
                display: flex;
                align-items: center;
                gap: 10px;
                font-weight: 600;
                font-size: 15px;
                color: var(--ma-text);
            }
            
            .assistant-logo {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                background: transparent;
                border-radius: var(--ma-radius-sm);
                color: var(--ma-text);
            }
            
            .assistant-logo svg {
                width: 20px;
                height: 20px;
            }
            
            .assistant-actions {
                display: flex;
                gap: 4px;
            }
            
            /* ========================================
               Persona Selector (Header)
               ======================================== */
            .assistant-persona-selector {
                position: relative;
                margin-left: auto;
                margin-right: 12px;
            }
            
            .assistant-persona-btn {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 10px;
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-sm);
                color: var(--ma-text);
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .assistant-persona-btn:hover {
                background: var(--ma-bg-tertiary);
                border-color: var(--ma-primary);
            }
            
            .assistant-persona-btn i,
            .assistant-persona-btn svg {
                width: 16px;
                height: 16px;
                color: var(--ma-text);
            }
            
            .assistant-persona-btn .persona-chevron {
                width: 14px;
                height: 14px;
                color: var(--ma-text-secondary);
                transition: transform 0.2s;
            }
            
            .assistant-persona-selector.open .persona-chevron {
                transform: rotate(180deg);
            }
            
            .assistant-persona-dropdown {
                position: absolute;
                top: 100%;
                left: 50%;
                transform: translateX(-50%) translateY(-8px);
                margin-top: 4px;
                min-width: 280px;
                max-width: 400px;
                width: max-content;
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius);
                box-shadow: var(--ma-shadow-lg);
                z-index: 100;
                opacity: 0;
                visibility: hidden;
                transition: var(--ma-transition);
            }
            
            .assistant-persona-selector.open .assistant-persona-dropdown {
                opacity: 1;
                visibility: visible;
                transform: translateX(-50%) translateY(0);
            }
            
            /* Ensure dropdown stays within panel bounds */
            .assistant-overlay .assistant-persona-dropdown {
                max-width: calc(100% - 24px);
                left: auto;
                right: -12px;
                transform: translateY(-8px);
            }
            
            .assistant-overlay .assistant-persona-selector.open .assistant-persona-dropdown {
                transform: translateY(0);
            }
            
            /* Small screen adjustments for persona dropdown */
            @media (max-width: 480px) {
                .assistant-persona-selector {
                    margin-right: 8px;
                }
                
                .assistant-persona-btn {
                    padding: 4px 8px;
                    font-size: 12px;
                }
                
                .assistant-persona-btn #assistant-persona-name {
                    max-width: 80px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .assistant-overlay .assistant-persona-dropdown {
                    right: -8px;
                    min-width: 200px;
                    max-width: calc(100% - 16px);
                }
                
                .persona-dropdown-item {
                    padding: 6px 8px;
                }
                
                .persona-dropdown-item .persona-icon {
                    width: 24px;
                    height: 24px;
                }
                
                .persona-dropdown-item .persona-name {
                    font-size: 12px;
                }
                
                .persona-dropdown-item .persona-desc {
                    font-size: 10px;
                }
            }
            
            .persona-dropdown-header {
                padding: 8px 12px;
                font-size: 11px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: var(--ma-text-secondary);
                border-bottom: 1px solid var(--ma-border);
            }
            
            .persona-dropdown-list {
                max-height: 200px;
                overflow-y: auto;
                padding: 4px;
            }
            
            .persona-dropdown-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 10px;
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .persona-dropdown-item:hover {
                background: var(--ma-bg-secondary);
            }
            
            .persona-dropdown-item.selected {
                background: rgba(0, 0, 0, 0.06);
            }
            
            .persona-dropdown-item .persona-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                border-radius: var(--ma-radius-xs);
                background: var(--ma-primary-light);
                color: var(--ma-text);
            }
            
            .persona-dropdown-item .persona-icon i,
            .persona-dropdown-item .persona-icon svg {
                width: 14px;
                height: 14px;
            }
            
            .persona-dropdown-item.selected .persona-icon {
                background: var(--ma-text);
                color: white;
            }
            
            .persona-dropdown-item .persona-info {
                flex: 1;
                min-width: 0;
            }
            
            .persona-dropdown-item .persona-name {
                font-size: 13px;
                font-weight: 500;
                color: var(--ma-text);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .persona-dropdown-item .persona-desc {
                font-size: 11px;
                color: var(--ma-text-secondary);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .persona-dropdown-item .persona-check {
                color: var(--ma-text);
                opacity: 0;
            }
            
            .persona-dropdown-item.selected .persona-check {
                opacity: 1;
            }
            
            .persona-loading {
                padding: 16px;
                text-align: center;
                color: var(--ma-text-secondary);
                font-size: 12px;
            }
            
            .assistant-btn-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border: none;
                background: transparent;
                color: var(--ma-text-secondary);
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .assistant-btn-icon:hover {
                background: var(--ma-bg-tertiary);
                color: var(--ma-text);
            }
            
            .assistant-btn-icon i,
            .assistant-btn-icon svg {
                width: 18px;
                height: 18px;
            }
            
            /* ========================================
               Tabs
               ======================================== */
            .assistant-tabs {
                display: flex;
                gap: 4px;
                padding: 8px 12px;
                background: var(--ma-bg-secondary);
                border-bottom: 1px solid var(--ma-border);
            }
            
            .assistant-tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 10px 12px;
                border: 1px solid transparent;
                background: transparent;
                color: var(--ma-text-secondary);
                border-radius: var(--ma-radius-sm);
                cursor: pointer;
                font-size: 13px;
                font-weight: 500;
                transition: var(--ma-transition);
            }
            
            .assistant-tab:hover {
                background: var(--ma-bg);
                color: var(--ma-text);
            }
            
            .assistant-tab.active {
                background: var(--ma-bg);
                color: var(--ma-text);
                border-color: var(--ma-border);
                box-shadow: var(--ma-shadow-sm);
                font-weight: 600;
            }
            
            .assistant-tab i,
            .assistant-tab svg {
                width: 16px;
                height: 16px;
            }
            
            .assistant-tab-label {
                display: none;
            }
            
            @media (min-width: 400px) {
                .assistant-tab-label {
                    display: inline;
                }
            }
            
            /* ========================================
               Content Area
               ======================================== */
            .assistant-content {
                flex: 1;
                overflow: hidden;
                position: relative;
                background: var(--ma-bg);
            }
            
            .assistant-tab-content {
                position: absolute;
                inset: 0;
                overflow: hidden;
                display: none;
            }
            
            .assistant-tab-content.active {
                display: flex;
                flex-direction: column;
            }
            
            /* Explorer tab needs visible overflow for floating windows, panels, and side menus */
            .assistant-tab-content[data-tab-content="explorer"] {
                overflow: visible;
            }
            
            /* ========================================
               Context Tab - Refined Design
               ======================================== */
            .context-panel {
                padding: 16px;
                overflow-y: auto;
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 16px;
            }
            
            .context-section {
                margin: 0;
            }
            
            .context-section-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 10px;
                color: var(--ma-text);
            }
            
            .context-section-header i,
            .context-section-header svg {
                width: 14px;
                height: 14px;
                color: var(--ma-text-secondary);
                opacity: 0.9;
            }
            
            .context-section-header h4 {
                margin: 0;
                font-size: 11px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.6px;
                color: var(--ma-text-secondary);
            }
            
            /* Selector Cards (Workspace, Canvas, Cursor) */
            .context-selector-card {
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border-light);
                border-radius: var(--ma-radius-sm);
                padding: 12px;
                transition: var(--ma-transition);
            }
            
            .context-selector-card:hover {
                border-color: var(--ma-border);
            }
            
            .context-selector-card .assistant-select {
                background: var(--ma-bg);
                margin-bottom: 0;
            }
            
            .context-selector-hint {
                display: block;
                margin-top: 8px;
                font-size: 11px;
                color: var(--ma-text-muted);
                line-height: 1.4;
            }
            
            /* Page Information Card */
            .context-info-card {
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border-light);
                border-radius: var(--ma-radius-sm);
                padding: 14px 16px;
            }
            
            .context-info-item {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }
            
            .context-info-divider {
                height: 1px;
                background: var(--ma-border-light);
                margin: 12px 0;
            }
            
            .context-info-label {
                font-size: 10px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: var(--ma-text-muted);
            }
            
            .context-info-value {
                font-size: 14px;
                color: var(--ma-text);
                word-break: break-word;
                line-height: 1.4;
            }
            
            .context-info-url {
                font-family: var(--ma-font-mono);
                font-size: 12px;
                color: var(--ma-text-secondary);
            }
            
            /* ========================================
               Context Rows - Responsive Layout
               ======================================== */
            
            /* Separator between sections */
            .context-separator {
                height: 1px;
                background: var(--ma-border-light);
                margin: 4px 0;
            }
            
            /* Row containers - stack vertically by default (minimized) */
            .context-row {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }
            
            /* Row 1: Workspace + Canvas */
            .context-row-1 {
                gap: 16px;
            }
            
            /* In minimized mode, hide canvas in row 1 (it appears in row 3) */
            .context-section-canvas-row1 {
                display: block;
            }
            
            /* Row 2: Page Info + Entities */
            .context-row-2 {
                gap: 16px;
            }
            
            .context-section-entities-group {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }
            
            .context-section-entities-group .context-btn-extract {
                width: 100%;
            }
            
            /* Row 3: Cursor + Add to Cursor */
            .context-row-3 {
                gap: 16px;
            }
            
            .context-section-cursor-group {
                display: flex;
                flex-direction: column;
                gap: 12px;
                flex: 1;
            }
            
            .context-section-cursor-action {
                display: flex;
                align-items: flex-end;
            }
            
            .context-section-cursor-action .assistant-btn {
                width: 100%;
            }
            
            /* Cursor Stats */
            .context-cursor-stats {
                padding: 8px 12px;
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border-light);
                border-radius: var(--ma-radius-sm);
            }
            
            .cursor-stats-placeholder {
                display: flex;
                gap: 16px;
                font-size: 12px;
                color: var(--ma-text-secondary);
            }
            
            .cursor-stat-item {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }
            
            .cursor-stat-item i,
            .cursor-stat-item svg {
                width: 14px;
                height: 14px;
                opacity: 0.7;
            }
            
            /* Row 4: Add to Graph button */
            .context-row-4 {
                margin-top: auto;
            }
            
            .context-row-4 .context-btn-graph {
                width: 100%;
            }
            
            /* ========================================
               Expanded Mode - Two Column Layout
               ======================================== */
            .merlin-assistant-overlay.expanded .context-row {
                flex-direction: row;
            }
            
            .merlin-assistant-overlay.expanded .context-row > .context-section,
            .merlin-assistant-overlay.expanded .context-row > .context-section-entities-group,
            .merlin-assistant-overlay.expanded .context-row > .context-section-cursor-group,
            .merlin-assistant-overlay.expanded .context-row > .context-section-cursor-action {
                flex: 1;
                min-width: 0;
            }
            
            /* Row 3 expanded: Cursor section takes more space */
            .merlin-assistant-overlay.expanded .context-row-3 .context-section-cursor-group {
                flex: 2;
            }
            
            .merlin-assistant-overlay.expanded .context-row-3 .context-section-cursor-action {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .merlin-assistant-overlay.expanded .context-section-cursor-action .assistant-btn {
                height: 100%;
                min-height: 80px;
            }
            
            /* Legacy support - keep old config row styles for backwards compat */
            .context-config-row {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }
            
            /* Expanded mode: side by side */
            .merlin-assistant-overlay.expanded .context-config-row {
                flex-direction: row;
            }
            
            .merlin-assistant-overlay.expanded .context-config-row .context-section {
                flex: 1;
                min-width: 0;
            }
            
            /* Detected Entities Section */
            .context-entities {
                min-height: 60px;
            }
            
            .context-empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 20px 16px;
                text-align: center;
                color: var(--ma-text-muted);
                background: var(--ma-bg-secondary);
                border: 1px dashed var(--ma-border);
                border-radius: var(--ma-radius-sm);
                transition: var(--ma-transition);
            }
            
            .context-empty-state:hover {
                border-color: var(--ma-border-light);
                background: var(--ma-bg-tertiary);
            }
            
            .context-empty-state i,
            .context-empty-state svg {
                width: 20px;
                height: 20px;
                margin-bottom: 8px;
                opacity: 0.6;
                color: var(--ma-text-muted);
            }
            
            .context-empty-state p {
                margin: 0;
                font-size: 12px;
                line-height: 1.4;
            }
            
            /* Entity List & Chips */
            .context-entity-list {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                padding: 4px;
            }
            
            .context-entity-chip {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 6px 12px 6px 10px;
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border-light);
                color: var(--ma-text);
                border-radius: 18px;
                font-size: 12px;
                font-weight: 500;
                transition: var(--ma-transition);
                cursor: default;
            }
            
            .context-entity-chip:hover {
                border-color: var(--ma-text);
                background: var(--ma-primary-light);
                color: var(--ma-text);
            }
            
            .context-entity-chip i,
            .context-entity-chip svg {
                width: 14px;
                height: 14px;
                opacity: 0.7;
            }
            
            /* Legacy Actions Panel - kept for backwards compat */
            .context-actions-panel {
                margin-top: auto;
                padding-top: 16px;
                border-top: 1px solid var(--ma-border-light);
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
            
            .context-actions-row {
                display: flex;
                gap: 10px;
            }
            
            .context-actions-row .assistant-btn {
                flex: 1;
            }
            
            .context-btn-plan {
                width: 100%;
            }
            
            /* Compact mode button text adjustments */
            .context-btn-extract span,
            .context-btn-stream span,
            .context-btn-plan span {
                white-space: nowrap;
            }
            
            /* Expanded mode: all buttons in one row */
            .merlin-assistant-overlay.expanded .context-actions-panel {
                flex-direction: row;
                gap: 10px;
            }
            
            .merlin-assistant-overlay.expanded .context-actions-row {
                flex: 2;
            }
            
            .merlin-assistant-overlay.expanded .context-btn-plan {
                flex: 1;
                width: auto;
            }
            
            /* Legacy support for old classes */
            .context-card {
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border-light);
                border-radius: var(--ma-radius-sm);
                padding: 14px;
            }
            
            .context-item {
                margin-bottom: 12px;
            }
            
            .context-item:last-child {
                margin-bottom: 0;
            }
            
            .context-label {
                display: block;
                font-size: 11px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: var(--ma-text-muted);
                margin-bottom: 4px;
            }
            
            .context-value {
                font-size: 14px;
                color: var(--ma-text);
                word-break: break-word;
            }
            
            .context-url {
                font-family: var(--ma-font-mono);
                font-size: 12px;
                color: var(--ma-text-secondary);
            }
            
            .context-hint {
                display: block;
                margin-top: 8px;
                font-size: 11px;
                color: var(--ma-text-muted);
            }
            
            .context-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                padding-top: 8px;
            }
            
            .context-actions .assistant-btn {
                flex: 1 1 calc(50% - 4px);
                min-width: 120px;
            }
            
            .context-actions .assistant-btn:last-child:nth-child(odd) {
                flex: 1 1 100%;
            }
            
            /* ========================================
               Buttons
               ======================================== */
            .assistant-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 10px 16px;
                border: none;
                border-radius: var(--ma-radius-sm);
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: var(--ma-transition);
                flex: 1;
            }
            
            .assistant-btn i,
            .assistant-btn svg {
                width: 16px;
                height: 16px;
            }
            
            .assistant-btn-primary {
                background: var(--ma-primary);
                color: white;
            }
            
            .assistant-btn-primary:hover {
                background: var(--ma-primary-hover);
            }
            
            .assistant-btn-secondary {
                background: var(--ma-bg-tertiary);
                color: var(--ma-text);
                border: 1px solid var(--ma-border);
            }
            
            .assistant-btn-secondary:hover {
                background: var(--ma-bg-secondary);
                border-color: var(--ma-border-light);
            }
            
            .assistant-btn-outline {
                background: transparent;
                color: var(--ma-text);
                border: 1px solid var(--ma-border);
            }
            
            .assistant-btn-outline:hover {
                background: var(--ma-bg-tertiary);
                border-color: var(--ma-primary);
                color: var(--ma-primary);
            }
            
            .assistant-btn-green {
                background: #15803d;
                color: white;
                border: none;
            }
            
            .assistant-btn-green:hover {
                background: #166534;
            }
            
            .assistant-btn-blue {
                background: #1d4ed8;
                color: white;
                border: none;
            }
            
            .assistant-btn-blue:hover {
                background: #1e40af;
            }
            
            /* ========================================
               Modal Styles
               ======================================== */
            .assistant-modal {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 100;
            }
            
            .assistant-modal-backdrop {
                position: absolute;
                inset: 0;
                background: rgba(0, 0, 0, 0.5);
            }
            
            .assistant-modal-content {
                position: relative;
                width: 90%;
                max-width: 400px;
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius);
                box-shadow: var(--ma-shadow);
            }
            
            .assistant-modal-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 20px;
                border-bottom: 1px solid var(--ma-border);
            }
            
            .assistant-modal-header h3 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: var(--ma-text);
            }
            
            .assistant-modal-body {
                padding: 20px;
            }
            
            .assistant-modal-footer {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                padding: 16px 20px;
                border-top: 1px solid var(--ma-border);
            }
            
            .form-group {
                margin-bottom: 16px;
            }
            
            .form-group:last-child {
                margin-bottom: 0;
            }
            
            .form-group label {
                display: block;
                margin-bottom: 6px;
                font-size: 13px;
                font-weight: 500;
                color: var(--ma-text-secondary);
            }
            
            .assistant-select {
                width: 100%;
                padding: 10px 12px;
                font-size: 14px;
                color: var(--ma-text);
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-sm);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .assistant-select:hover {
                border-color: var(--ma-border-light);
            }
            
            .assistant-select:focus {
                outline: none;
                border-color: var(--ma-primary);
                box-shadow: 0 0 0 3px var(--ma-primary-light);
            }

            /* ========================================
               Chat Container
               ======================================== */
            .assistant-chat-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }
            
            .chat-loading,
            .graph-loading {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: var(--ma-text-muted);
            }
            
            .chat-loading-spinner,
            .graph-loading-spinner {
                width: 32px;
                height: 32px;
                border: 3px solid var(--ma-border);
                border-top-color: var(--ma-primary);
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
                margin-bottom: 12px;
            }
            
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            
            /* ========================================
               Graph Container - Full GraphExplorer
               
               Z-index hierarchy (must match base explorer styles):
                 2000  toolbar dropdown menus
                 1001  settings card header, tab bar overlays
                 1000  explorer tabs, tooltips
                  600  toolbars (left / right)
                  500  floating windows, task windows
                   60  overlay panels (side menus)
                   50  overlay backdrop
               ======================================== */
            .assistant-graph-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: visible;
                position: relative;
            }
            
            /* Embedded GraphExplorer container */
            .graph-explorer-embedded {
                width: 100%;
                height: 100%;
                min-height: 400px;
                position: relative;
                overflow: visible;
            }
            
            /* Override GraphExplorer styles when embedded */
            .assistant-graph-container .graph-explorer {
                position: relative !important;
                width: 100% !important;
                height: 100% !important;
                min-height: 100% !important;
                border-radius: 0;
                overflow: visible;
            }
            
            /* Ensure the network canvas fills the container - toolbar overlays on top */
            .assistant-graph-container #mynetwork {
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
            }
            
            /* Toolbars — always visible, auto-sized, above empty state (z-index: 50) */
            .assistant-graph-container .toolbar {
                display: flex !important;
                visibility: visible !important;
                opacity: 1 !important;
                z-index: 600 !important;
                height: auto !important;
                max-height: calc(100% - 24px) !important;
            }
            
            .assistant-graph-container .toolbar-left {
                position: absolute !important;
                top: 50% !important;
                transform: translateY(-50%) !important;
                left: 8px !important;
            }
            
            .assistant-graph-container .toolbar-right {
                position: absolute !important;
                top: 50% !important;
                transform: translateY(-50%) !important;
                right: 8px !important;
                left: auto !important;
            }
            
            /* Override fixed positioning for elements that should stay in container */
            .assistant-graph-container .close-modal-btn,
            .assistant-graph-container .api-warning-notification {
                position: absolute !important;
            }
            
            /* Ensure empty state fills the container - toolbar overlays on top */
            .assistant-graph-container .empty-state-overlay {
                position: absolute !important;
                left: 0 !important;
            }
            
            /* Settings window layout fixes */
            .assistant-graph-container .settings-layout-grid {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 6px !important;
            }
            
            .assistant-graph-container .settings-layout-btn {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                gap: 4px !important;
                padding: 10px 6px !important;
                border: 1px solid var(--explorer-border, rgba(255,255,255,0.1)) !important;
                border-radius: 8px !important;
                background: transparent !important;
                color: var(--explorer-text-muted, rgba(255,255,255,0.6)) !important;
                font-size: 10px !important;
                cursor: pointer !important;
            }
            
            .assistant-graph-container .settings-layout-btn.active {
                background: rgba(255, 255, 255, 0.08) !important;
                border-color: var(--explorer-accent, #c8c8c8) !important;
                color: var(--explorer-accent, #c8c8c8) !important;
            }
            
            .assistant-graph-container .settings-toggle-row {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
            }
            
            .assistant-graph-container .settings-toggle-switch {
                width: 36px !important;
                height: 20px !important;
                background: var(--explorer-border, rgba(255,255,255,0.1)) !important;
                border-radius: 10px !important;
                position: relative !important;
                display: inline-block !important;
            }
            
            .assistant-graph-container .settings-slider-row {
                display: flex !important;
                align-items: center !important;
                gap: 10px !important;
            }
            
            .assistant-graph-container .settings-slider {
                flex: 1 !important;
                height: 4px !important;
                -webkit-appearance: none !important;
                appearance: none !important;
                background: var(--explorer-border, rgba(255,255,255,0.1)) !important;
                border-radius: 2px !important;
            }
            
            .assistant-graph-container .settings-slider-value {
                font-size: 11px !important;
                min-width: 36px !important;
                text-align: right !important;
            }
            
            .assistant-graph-container .settings-group {
                margin-bottom: 16px !important;
                padding-bottom: 16px !important;
                border-bottom: 1px solid var(--explorer-border, rgba(255,255,255,0.1)) !important;
            }
            
            .assistant-graph-container .settings-group:last-child {
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
                border-bottom: none !important;
            }
            
            .assistant-graph-container .settings-group-title {
                font-size: 11px !important;
                font-weight: 600 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                color: var(--explorer-text-muted, rgba(255,255,255,0.6)) !important;
                margin-bottom: 12px !important;
            }
            
            .assistant-graph-container .settings-select {
                width: 100% !important;
                padding: 8px 10px !important;
                border: 1px solid var(--explorer-border, rgba(255,255,255,0.1)) !important;
                border-radius: 6px !important;
                background: rgba(0, 0, 0, 0.2) !important;
                color: var(--explorer-text, #f8f9fa) !important;
                font-size: 12px !important;
            }
            
            .assistant-graph-container .floating-window {
                max-height: calc(100% - 40px) !important;
            }
            
            /* Bottom bar positioning */
            .assistant-graph-container .bottom-bar {
                position: absolute !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
            }
            
            /* Floating windows within embedded graph - constrain to visible area */
            .assistant-graph-container #floating-windows {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
            }
            
            .assistant-graph-container .floating-window {
                max-height: calc(100% - 20px);
                overflow-y: auto;
            }
            
            /* Overlay panels (side menus) — keep z-index: 60 from panels.css (below floating-windows: 500) */
            .assistant-graph-container .overlay-panel.from-left,
            .assistant-graph-container .overlay-panel.from-right {
                position: absolute !important;
                top: 0 !important;
                bottom: 0 !important;
            }
            
            /* ========================================
               Chat-open adjustments for embedded graph
               
               The base graph CSS (.graph-explorer.chat-open) shifts
               elements so they stay inside the graph column when the
               built-in Agents/chat panel opens on the right.
               
               Our !important overrides above block those shifts, so we
               re-apply them here with matching specificity + !important.
               ======================================== */
            .assistant-graph-container .graph-explorer.chat-open #mynetwork {
                right: var(--chat-w) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .toolbar-right.toolbar-global {
                right: calc(var(--chat-w) + 8px) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .toolbar-chat-toggle {
                right: calc(var(--chat-w) + 8px) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .explorer-tabs {
                left: calc(50% - var(--chat-w) / 2) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .bottom-subtabs {
                left: calc(50% - var(--chat-w) / 2) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .tab-content {
                right: var(--chat-w) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open #floating-windows {
                right: var(--chat-w) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .bottom-bar {
                right: var(--chat-w) !important;
            }
            
            .assistant-graph-container .graph-explorer.chat-open .empty-state-overlay,
            .assistant-graph-container .graph-explorer.chat-open .legend-panel,
            .assistant-graph-container .graph-explorer.chat-open .fql-panel,
            .assistant-graph-container .graph-explorer.chat-open #loadingBar {
                right: var(--chat-w) !important;
            }
            
            /* ========================================
               Integrated Chat Panel Styles
               ======================================== */
            .assistant-chat-container .integrated-chat {
                display: flex;
                flex-direction: column;
                height: 100%;
            }
            
            .integrated-chat-messages {
                flex: 1;
                overflow-y: auto;
                padding: 16px;
            }
            
            .integrated-chat-message {
                margin-bottom: 16px;
                animation: messageIn 0.2s ease-out;
            }
            
            @keyframes messageIn {
                from {
                    opacity: 0;
                    transform: translateY(8px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            .integrated-chat-message.user {
                display: flex;
                justify-content: flex-end;
            }
            
            .integrated-chat-message.user .message-bubble {
                background: var(--ma-primary);
                color: white;
                border-radius: 16px 16px 4px 16px;
                max-width: 85%;
            }
            
            .integrated-chat-message.assistant .message-bubble {
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border);
                border-radius: 16px 16px 16px 4px;
                max-width: 85%;
            }
            
            .message-bubble {
                padding: 12px 16px;
                font-size: 14px;
                line-height: 1.5;
            }
            
            .message-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
                font-size: 12px;
                color: var(--ma-text-muted);
            }
            
            /* Typing indicator animation */
            .typing-indicator {
                display: flex;
                gap: 4px;
                padding: 4px 0;
            }
            
            .typing-indicator span {
                width: 8px;
                height: 8px;
                background: var(--ma-text-muted);
                border-radius: 50%;
                animation: typing-bounce 1.4s ease-in-out infinite;
            }
            
            .typing-indicator span:nth-child(1) { animation-delay: 0ms; }
            .typing-indicator span:nth-child(2) { animation-delay: 200ms; }
            .typing-indicator span:nth-child(3) { animation-delay: 400ms; }
            
            @keyframes typing-bounce {
                0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                30% { transform: translateY(-6px); opacity: 1; }
            }
            
            /* Error message style */
            .message-bubble.error {
                background: var(--ma-danger-bg, #fef2f2);
                border-color: var(--ma-danger, #ef4444);
                color: var(--ma-danger-text, #991b1b);
            }
            
            .integrated-chat-input {
                padding: 16px;
                border-top: 1px solid var(--ma-border);
                background: var(--ma-bg);
            }
            
            .chat-input-wrapper {
                display: flex;
                gap: 8px;
                background: var(--ma-bg-secondary);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-sm);
                padding: 4px;
                transition: var(--ma-transition);
            }
            
            .chat-input-wrapper:focus-within {
                border-color: var(--ma-primary);
                box-shadow: 0 0 0 3px var(--ma-primary-light);
            }
            
            .chat-input-wrapper input {
                flex: 1;
                border: none;
                background: transparent;
                padding: 10px 12px;
                font-size: 14px;
                color: var(--ma-text);
                outline: none;
            }
            
            .chat-input-wrapper input::placeholder {
                color: var(--ma-text-muted);
            }
            
            .chat-send-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                border: none;
                background: var(--ma-primary);
                color: white;
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .chat-send-btn:hover {
                background: var(--ma-primary-hover);
            }
            
            .chat-send-btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
            
            .chat-send-btn i,
            .chat-send-btn svg {
                width: 18px;
                height: 18px;
            }
            
            /* Search suggestions */
            .chat-search-container {
                position: relative;
            }
            
            .chat-suggestions {
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-sm);
                box-shadow: var(--ma-shadow);
                max-height: 240px;
                overflow-y: auto;
                margin-bottom: 8px;
                z-index: 10;
            }
            
            .suggestion-item {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px;
                cursor: pointer;
                border-bottom: 1px solid var(--ma-border-light);
                transition: var(--ma-transition);
            }
            
            .suggestion-item:last-child {
                border-bottom: none;
            }
            
            .suggestion-item:hover {
                background: var(--ma-bg-secondary);
            }
            
            .suggestion-icon {
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--ma-primary-light);
                color: var(--ma-primary);
                border-radius: var(--ma-radius-xs);
            }
            
            .suggestion-icon i,
            .suggestion-icon svg {
                width: 16px;
                height: 16px;
            }
            
            .suggestion-content {
                flex: 1;
                min-width: 0;
            }
            
            .suggestion-title {
                font-weight: 500;
                color: var(--ma-text);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .suggestion-type {
                font-size: 12px;
                color: var(--ma-text-muted);
            }
            
            /* ========================================
               Integrated Graph Explorer Styles
               ======================================== */
            .assistant-graph-container .integrated-graph {
                display: flex;
                flex-direction: column;
                height: 100%;
            }
            
            .graph-toolbar {
                display: flex;
                gap: 4px;
                padding: 12px;
                border-bottom: 1px solid var(--ma-border);
                background: var(--ma-bg);
            }
            
            .graph-toolbar-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                border: 1px solid var(--ma-border);
                background: var(--ma-bg);
                color: var(--ma-text-secondary);
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .graph-toolbar-btn:hover {
                background: var(--ma-bg-secondary);
                color: var(--ma-text);
            }
            
            .graph-toolbar-btn.active {
                background: var(--ma-primary-light);
                color: var(--ma-primary);
                border-color: var(--ma-primary);
            }
            
            .graph-toolbar-btn i,
            .graph-toolbar-btn svg {
                width: 18px;
                height: 18px;
            }
            
            .graph-toolbar-divider {
                width: 1px;
                background: var(--ma-border);
                margin: 0 4px;
            }
            
            .graph-canvas {
                flex: 1;
                position: relative;
                background: var(--ma-bg-secondary);
                min-height: 400px;
            }
            
            .graph-canvas #mynetwork {
                width: 100%;
                height: 100%;
                min-height: 400px;
            }
            
            .graph-status {
                position: absolute;
                bottom: 12px;
                left: 12px;
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-xs);
                font-size: 12px;
                color: var(--ma-text-secondary);
            }
            
            .graph-status-dot {
                width: 8px;
                height: 8px;
                background: var(--ma-success);
                border-radius: 50%;
            }
            
            /* ========================================
               Empty States
               ======================================== */
            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 32px;
                text-align: center;
            }
            
            .empty-state-icon {
                width: 64px;
                height: 64px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--ma-bg-secondary);
                border-radius: 50%;
                margin-bottom: 16px;
            }
            
            .empty-state-icon i,
            .empty-state-icon svg {
                width: 32px;
                height: 32px;
                color: var(--ma-text-muted);
            }
            
            .empty-state h3 {
                margin: 0 0 8px;
                font-size: 16px;
                color: var(--ma-text);
            }
            
            .empty-state p {
                margin: 0;
                font-size: 14px;
                color: var(--ma-text-secondary);
            }
            
            /* ========================================
               Artefacts Panel
               ======================================== */
            .assistant-artefacts-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }
            
            .artefacts-panel {
                flex: 1;
                overflow-y: auto;
                padding: 16px;
            }
            
            .artefacts-section {
                margin-bottom: 24px;
            }
            
            .artefacts-section-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                padding-bottom: 8px;
                border-bottom: 1px solid var(--ma-border-light);
            }
            
            .artefacts-section-header i,
            .artefacts-section-header svg {
                width: 18px;
                height: 18px;
                color: var(--ma-text-secondary);
            }
            
            .artefacts-section-header h4 {
                flex: 1;
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: var(--ma-text);
            }
            
            .artefacts-refresh-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                border: none;
                background: transparent;
                color: var(--ma-text-muted);
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
            }
            
            .artefacts-refresh-btn:hover {
                background: var(--ma-bg-tertiary);
                color: var(--ma-primary);
            }
            
            .artefacts-refresh-btn i,
            .artefacts-refresh-btn svg {
                width: 14px;
                height: 14px;
            }
            
            .artefacts-list {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
            
            .artefacts-empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 24px;
                text-align: center;
                color: var(--ma-text-muted);
                background: var(--ma-bg-secondary);
                border: 1px dashed var(--ma-border);
                border-radius: var(--ma-radius-sm);
            }
            
            .artefacts-empty-state.error {
                border-color: var(--ma-error);
                background: rgba(239, 68, 68, 0.05);
            }
            
            .artefacts-empty-state i,
            .artefacts-empty-state svg {
                width: 24px;
                height: 24px;
                margin-bottom: 8px;
                opacity: 0.5;
            }
            
            .artefacts-empty-state p {
                margin: 0;
                font-size: 13px;
            }

            .artefacts-empty-hint {
                margin-top: 4px;
                font-size: 11px;
                color: var(--ma-text-muted);
                opacity: 0.7;
            }
            
            .artefacts-loading-inline {
                display: flex;
                justify-content: center;
                padding: 16px;
            }
            
            .spinner-small {
                width: 20px;
                height: 20px;
                border: 2px solid var(--ma-border);
                border-top-color: var(--ma-primary);
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
            }
            
            .artefacts-loading,
            .artefacts-loading-spinner {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: var(--ma-text-muted);
            }
            
            .artefacts-loading-spinner {
                width: 32px;
                height: 32px;
                border: 3px solid var(--ma-border);
                border-top-color: var(--ma-primary);
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
                margin-bottom: 12px;
            }
            
            /* Artefact Cards */
            .artefact-card {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px;
                background: var(--ma-bg);
                border: 1px solid var(--ma-border);
                border-radius: var(--ma-radius-sm);
                transition: var(--ma-transition);
            }
            
            .artefact-card:hover {
                border-color: var(--ma-primary);
                background: var(--ma-bg-secondary);
                box-shadow: var(--ma-shadow-sm);
            }
            
            .artefact-card-icon {
                flex-shrink: 0;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: var(--ma-radius-xs);
                background: var(--ma-bg-tertiary);
                color: var(--ma-text-secondary);
            }
            
            .artefact-card-icon.type-warning {
                background: rgba(239, 68, 68, 0.1);
                color: #EF4444;
            }
            
            .artefact-card-icon.type-info {
                background: rgba(6, 182, 212, 0.1);
                color: #06B6D4;
            }
            
            .artefact-card-icon.type-primary {
                background: rgba(59, 130, 246, 0.1);
                color: #3B82F6;
            }
            
            .artefact-card-icon.type-success {
                background: rgba(16, 185, 129, 0.1);
                color: #10B981;
            }
            
            .artefact-card-icon.type-purple {
                background: rgba(139, 92, 246, 0.1);
                color: #8B5CF6;
            }
            
            .artefact-card-icon i,
            .artefact-card-icon svg {
                width: 18px;
                height: 18px;
            }
            
            .artefact-card-content {
                flex: 1;
                min-width: 0;
            }
            
            .artefact-card-title {
                font-size: 14px;
                font-weight: 500;
                color: var(--ma-text);
                margin-bottom: 4px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .artefact-card-meta {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 11px;
                color: var(--ma-text-muted);
            }
            
            .artefact-card-type {
                padding: 2px 6px;
                background: var(--ma-bg-tertiary);
                border-radius: 4px;
                text-transform: capitalize;
            }
            
            .artefact-card-size {
                padding: 2px 6px;
                background: var(--ma-bg-tertiary);
                color: var(--ma-text-secondary);
                border-radius: 4px;
            }
            
            .artefact-card-download {
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border: none;
                background: transparent;
                color: var(--ma-text-muted);
                border-radius: var(--ma-radius-xs);
                cursor: pointer;
                transition: var(--ma-transition);
                opacity: 0;
            }
            
            .artefact-card:hover .artefact-card-download {
                opacity: 1;
            }
            
            .artefact-card-download:hover {
                background: var(--ma-primary-light);
                color: var(--ma-primary);
            }
            
            .artefact-card-download i,
            .artefact-card-download svg {
                width: 16px;
                height: 16px;
            }
            
            /* ========================================
               Scrollbar
               ======================================== */
            .assistant-tab-content ::-webkit-scrollbar,
            .integrated-chat-messages::-webkit-scrollbar {
                width: 6px;
            }
            
            .assistant-tab-content ::-webkit-scrollbar-track,
            .integrated-chat-messages::-webkit-scrollbar-track {
                background: transparent;
            }
            
            .assistant-tab-content ::-webkit-scrollbar-thumb,
            .integrated-chat-messages::-webkit-scrollbar-thumb {
                background: var(--ma-border);
                border-radius: 3px;
            }
            
            .assistant-tab-content ::-webkit-scrollbar-thumb:hover,
            .integrated-chat-messages::-webkit-scrollbar-thumb:hover {
                background: var(--ma-text-muted);
            }
        `;

        document.head.appendChild(style);
    }

    _setupEventHandlers() {
        // Guard against null container
        if (!this.container) {
            console.warn('[AssistantOverlay] Container not available for _setupEventHandlers');
            return;
        }
        
        // Close button
        const closeBtn = this.container.querySelector('#assistant-close-btn');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.toggle(false));
        }

        // Expand button
        const expandBtn = this.container.querySelector('#assistant-expand-btn');
        if (expandBtn) {
            expandBtn.addEventListener('click', () => this.toggleExpanded());
        }

        // Refresh button
        const refreshBtn = this.container.querySelector('#assistant-refresh-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.refresh());
        }

        // Theme toggle
        const themeBtn = this.container.querySelector('#assistant-theme-btn');
        if (themeBtn) {
            themeBtn.addEventListener('click', () => {
                const newTheme = this.options.theme === 'dark' ? 'light' : 'dark';
                setTheme(newTheme);
                this.setTheme(newTheme);
            });
        }

        // Tab switching
        const tabs = this.container.querySelectorAll('.assistant-tab[data-tab]');
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                this.setActiveTab(tab.dataset.tab);
            });
        });

        // Context actions
        const extractBtn = this.container.querySelector('#ctx-extract-btn');
        if (extractBtn) {
            extractBtn.addEventListener('click', () => this._extractEntities());
        }

        const addCursorBtn = this.container.querySelector('#ctx-add-cursor-btn');
        if (addCursorBtn) {
            addCursorBtn.addEventListener('click', () => this._addToCursor());
        }
        
        const addGraphBtn = this.container.querySelector('#ctx-add-graph-btn');
        if (addGraphBtn) {
            addGraphBtn.addEventListener('click', () => this._showGraphModal());
        }
        
        // Workspace and Canvas selectors
        const workspaceSelect = this.container.querySelector('#ctx-workspace-select');
        if (workspaceSelect) {
            workspaceSelect.addEventListener('change', (e) => this._onWorkspaceChange(e.target.value));
            // Load workspaces on init
            this._loadWorkspaces();
        } else if (this.options.workspace_id) {
            // Workspace is pre-configured (env var), load canvases directly
            this._loadCanvases(this.options.workspace_id);
        }
        
        const canvasSelect = this.container.querySelector('#ctx-canvas-select');
        if (canvasSelect) {
            canvasSelect.addEventListener('change', (e) => this._onCanvasChange(e.target.value));
        }
        
        const cursorSelect = this.container.querySelector('#ctx-cursor-select');
        if (cursorSelect) {
            cursorSelect.addEventListener('change', (e) => this._onCursorChange(e.target.value));
            // Cursors are loaded when a canvas is selected - no init load needed
        }
        
        // Graph modal handlers
        const modalBackdrop = this.container.querySelector('.assistant-modal-backdrop');
        const modalCloseBtn = this.container.querySelector('#modal-close-btn');
        const modalCancelBtn = this.container.querySelector('#modal-cancel-btn');
        const modalConfirmBtn = this.container.querySelector('#modal-confirm-btn');
        
        if (modalBackdrop) modalBackdrop.addEventListener('click', () => this._hideGraphModal());
        if (modalCloseBtn) modalCloseBtn.addEventListener('click', () => this._hideGraphModal());
        if (modalCancelBtn) modalCancelBtn.addEventListener('click', () => this._hideGraphModal());
        if (modalConfirmBtn) modalConfirmBtn.addEventListener('click', () => this._addToGraph());

        // Persona selector dropdown
        const personaBtn = this.container.querySelector('#assistant-persona-btn');
        const personaSelector = this.container.querySelector('.assistant-persona-selector');
        if (personaBtn && personaSelector) {
            personaBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                personaSelector.classList.toggle('open');
                if (personaSelector.classList.contains('open')) {
                    this._loadPersonas();
                }
            });
            
            // Close dropdown when clicking outside
            document.addEventListener('click', (e) => {
                if (!personaSelector.contains(e.target)) {
                    personaSelector.classList.remove('open');
                }
            });
        }

        // Keyboard shortcut (Escape to close)
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.toggle(false);
            }
        });
    }
    
    /**
     * Load personas for the header selector
     */
    async _loadPersonas() {
        const listEl = this.container.querySelector('#assistant-persona-list');
        if (!listEl) return;
        
        // Use selectedWorkspaceId if available (from Context tab), fallback to options
        const workspaceId = this.selectedWorkspaceId || this.options.workspace_id;
        
        // Check for valid config - api_endpoint can be empty string (for proxy mode)
        if (this.options.api_endpoint === null || this.options.api_endpoint === undefined || !workspaceId) {
            safeSetInnerHTML(listEl, '<div class="persona-loading">No workspace configured</div>');
            return;
        }
        
        try {
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${workspaceId}/personas/`,
                { credentials: 'include', headers }
            );
            
            if (!response.ok) throw new Error('Failed to load personas');
            
            const data = await response.json();
            const personas = Array.isArray(data) ? data : (data.items || []);
            
            if (personas.length === 0) {
                safeSetInnerHTML(listEl, '<div class="persona-loading">No personas available</div>');
                return;
            }
            
            // Auto-select "Me" persona as default if nothing is selected
            if (!this.selectedPersonaId) {
                // Look for persona named "Me" first, then fall back to first system persona, then first in list
                const mePersona = personas.find(p => p.name === 'Me') ||
                                  personas.find(p => p.is_system) ||
                                  personas[0];
                if (mePersona) {
                    this.selectedPersonaId = mePersona.id;
                    // Update the header button text
                    const nameEl = this.container.querySelector('#assistant-persona-name');
                    if (nameEl) nameEl.textContent = mePersona.name;
                    // Propagate to A2UIChat if active
                    if (this.a2uiChat && typeof this.a2uiChat.setPersona === 'function') {
                        this.a2uiChat.setPersona(mePersona.id, mePersona.name);
                    }
                }
            }
            
            safeSetInnerHTML(listEl, personas.map(persona => `
                <div class="persona-dropdown-item ${persona.id === this.selectedPersonaId ? 'selected' : ''}" 
                     data-persona-id="${persona.id}">
                    <div class="persona-icon">
                        <i data-lucide="${persona.is_system ? 'user' : 'user-circle'}"></i>
                    </div>
                    <div class="persona-info">
                        <div class="persona-name">${this._escapeHtml(persona.name)}</div>
                        ${persona.description ? `<div class="persona-desc">${this._escapeHtml(persona.description)}</div>` : ''}
                    </div>
                    <i data-lucide="check" class="persona-check"></i>
                </div>
            `).join(''));
            
            // Add click handlers
            listEl.querySelectorAll('.persona-dropdown-item').forEach(item => {
                item.addEventListener('click', () => {
                    const personaId = item.dataset.personaId;
                    const personaName = item.querySelector('.persona-name')?.textContent || 'Unknown';
                    this._selectPersona(personaId, personaName);
                });
            });
            
            // Refresh icons
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
            
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load personas:', err);
            safeSetInnerHTML(listEl, '<div class="persona-loading">Failed to load personas</div>');
        }
    }
    
    /**
     * Load workspaces for the Context tab dropdown
     */
    async _loadWorkspaces() {
        const selectEl = this.container.querySelector('#ctx-workspace-select');
        if (!selectEl) return;
        
        // api_endpoint can be empty string for proxy mode
        if (this.options.api_endpoint === null || this.options.api_endpoint === undefined) {
            return;
        }
        
        try {
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/`,
                { credentials: 'include', headers }
            );
            
            if (!response.ok) {
                console.warn('[AssistantOverlay] Failed to load workspaces:', response.status);
                return;
            }
            
            const workspaces = await response.json();
            
            // Clear existing options except default
            safeSetInnerHTML(selectEl, '<option value="">Select workspace...</option>');
            
            workspaces.forEach(ws => {
                const option = document.createElement('option');
                option.value = ws.id;
                option.textContent = ws.name || ws.id;
                selectEl.appendChild(option);
            });
            
            // Restore from localStorage or use current workspace
            const savedWorkspaceId = localStorage.getItem('assistant_workspace_id');
            if (savedWorkspaceId && workspaces.some(ws => ws.id === savedWorkspaceId)) {
                selectEl.value = savedWorkspaceId;
                this._onWorkspaceChange(savedWorkspaceId, false);  // false = don't save again
            } else if (this.options.workspace_id) {
                selectEl.value = this.options.workspace_id;
                this._onWorkspaceChange(this.options.workspace_id, false);
            }
            
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load workspaces:', err);
        }
    }
    
    /**
     * Handle workspace selection change
     */
    async _onWorkspaceChange(workspaceId, save = true) {
        this.selectedWorkspaceId = workspaceId || null;
        
        // IMPORTANT: Also update options.workspace_id so personas and other features use the new value
        this.options.workspace_id = workspaceId || null;
        
        if (save && workspaceId) {
            localStorage.setItem('assistant_workspace_id', workspaceId);
        } else if (save) {
            localStorage.removeItem('assistant_workspace_id');
        }
        
        // Reset cursor selector to disabled state until canvas is selected
        const cursorSelect = this.container.querySelector('#ctx-cursor-select');
        if (cursorSelect) {
            cursorSelect.disabled = true;
            safeSetInnerHTML(cursorSelect, '<option value="">Select a canvas first</option>');
        }
        this.selectedCursorId = null;
        this.selectedCanvasId = null;
        
        // Refresh personas since workspace changed
        await this._loadPersonas();
        
        // Update A2UIChat if it's initialized
        console.log('[AssistantOverlay] Checking if a2uiChat exists:', !!this.a2uiChat, 'workspaceId:', workspaceId);
        if (this.a2uiChat && workspaceId) {
            try {
                console.log('[AssistantOverlay] Calling a2uiChat.setWorkspace');
                await this.a2uiChat.setWorkspace(workspaceId, this.options.api_endpoint, this.options.access_token);
            } catch (err) {
                console.warn('[AssistantOverlay] Failed to sync workspace to chat:', err);
            }
        }
        
        // Update Graph Explorer if it's initialized
        if (this.graphExplorer && workspaceId) {
            try {
                console.log('[AssistantOverlay] Propagating workspace change to GraphExplorer:', workspaceId);
                if (typeof this.graphExplorer.setWorkspace === 'function') {
                    // MerlinGraph.setWorkspace handles ExplorerAPI sync, cursor reset, data reload
                    await this.graphExplorer.setWorkspace(workspaceId);
                } else {
                    // Fallback: update option and force full reload
                    this.graphExplorer.options.workspace_id = workspaceId;
                    this._graphConfig = { ...this._graphConfig, workspaceId };
                    if (this.activeTab === 'explorer') {
                        await this._loadGraphDataIntoExplorer();
                    }
                }
            } catch (err) {
                console.warn('[AssistantOverlay] Failed to sync workspace to explorer:', err);
            }
        }
        
        // Load canvases for this workspace
        if (workspaceId) {
            // Reset the 'me' cursor for this workspace
            await this._resetMeCursor(workspaceId);
            
            await this._loadCanvases(workspaceId);
        } else {
            // Clear canvas dropdown
            const canvasSelect = this.container.querySelector('#ctx-canvas-select');
            if (canvasSelect) {
                safeSetInnerHTML(canvasSelect, '<option value="">No canvas found</option>');
            }
            this._onCanvasChange('', false);
        }
        
        // Load cursor stats for the 'me' cursor
        await this._loadCursorStats();
        
        console.log('[AssistantOverlay] Workspace changed:', workspaceId);
    }
    
    /**
     * Reset the 'me' cursor for a workspace
     * Called when workspace is loaded to ensure clean state
     */
    async _resetMeCursor(workspaceId) {
        if (!this._hasApiEndpoint() || !workspaceId) return;
        
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${workspaceId}/cursors/me/reset`,
                { 
                    method: 'POST',
                    credentials: 'include', 
                    headers 
                }
            );
            
            if (response.ok) {
                console.log('[AssistantOverlay] Reset me cursor for workspace:', workspaceId);
            } else {
                console.warn('[AssistantOverlay] Failed to reset me cursor:', response.status);
            }
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to reset me cursor:', err);
        }
    }
    
    /**
     * Load canvases for the Context tab dropdown
     */
    async _loadCanvases(workspaceId = null) {
        const selectEl = this.container.querySelector('#ctx-canvas-select');
        if (!selectEl) return;
        
        const wsId = workspaceId || this.selectedWorkspaceId || this.options.workspace_id;
        // api_endpoint can be empty string for proxy mode
        if ((this.options.api_endpoint === null || this.options.api_endpoint === undefined) || !wsId) {
            return;
        }
        
        try {
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${wsId}/canvas/`,
                { credentials: 'include', headers }
            );
            
            if (!response.ok) {
                console.warn('[AssistantOverlay] Failed to load canvases:', response.status);
                return;
            }
            
            const canvases = await response.json();
            
            // Clear existing options
            if (canvases.length === 0) {
                safeSetInnerHTML(selectEl, '<option value="">No canvas found</option>');
                // Keep cursor disabled when no canvases
                const cursorSelect = this.container.querySelector('#ctx-cursor-select');
                if (cursorSelect) {
                    cursorSelect.disabled = true;
                    safeSetInnerHTML(cursorSelect, '<option value="">No canvas available</option>');
                }
                return;
            }
            
            safeSetInnerHTML(selectEl, '<option value="">Select a canvas...</option>');
            
            canvases.forEach(canvas => {
                const option = document.createElement('option');
                option.value = canvas.id;
                option.textContent = canvas.name || canvas.id;
                selectEl.appendChild(option);
            });
            
            // Restore from localStorage
            const savedCanvasId = localStorage.getItem('assistant_canvas_id');
            if (savedCanvasId && canvases.some(c => c.id === savedCanvasId)) {
                selectEl.value = savedCanvasId;
                this._onCanvasChange(savedCanvasId, false);  // false = don't save again
            }
            
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load canvases:', err);
        }
    }
    
    /**
     * Handle canvas selection change
     */
    async _onCanvasChange(canvasId, save = true) {
        this.selectedCanvasId = canvasId || null;
        
        if (save && canvasId) {
            localStorage.setItem('assistant_canvas_id', canvasId);
        } else if (save) {
            localStorage.removeItem('assistant_canvas_id');
        }
        
        // Propagate to A2UIChat if active
        if (this.a2uiChat && typeof this.a2uiChat.setCanvas === 'function') {
            this.a2uiChat.setCanvas(canvasId);
        }
        
        // Enable/disable cursor selector based on canvas selection
        const cursorSelect = this.container.querySelector('#ctx-cursor-select');
        if (cursorSelect) {
            if (canvasId) {
                // Canvas selected - enable cursor selection and load cursors
                cursorSelect.disabled = false;
                await this._loadCursors(canvasId);
            } else {
                // No canvas selected - disable cursor selection
                cursorSelect.disabled = true;
                safeSetInnerHTML(cursorSelect, '<option value="">Select a canvas first</option>');
                this.selectedCursorId = null;
            }
        }
        
        console.log('[AssistantOverlay] Canvas changed:', canvasId);
    }
    
    /**
     * Load cursors for the Context tab dropdown
     * @param {string} canvasId - Canvas ID to filter cursors by
     */
    async _loadCursors(canvasId = null) {
        const selectEl = this.container.querySelector('#ctx-cursor-select');
        if (!selectEl) return;
        
        const wsId = this.selectedWorkspaceId || this.options.workspace_id;
        if (!this._hasApiEndpoint() || !wsId) {
            safeSetInnerHTML(selectEl, '<option value="">No workspace selected</option>');
            selectEl.disabled = true;
            return;
        }
        
        // Canvas is required for cursor selection
        const activeCanvasId = canvasId || this.selectedCanvasId;
        if (!activeCanvasId) {
            safeSetInnerHTML(selectEl, '<option value="">Select a canvas first</option>');
            selectEl.disabled = true;
            return;
        }
        
        safeSetInnerHTML(selectEl, '<option value="">Loading cursors...</option>');
        
        try {
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            // Get cursors filtered by canvas
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${wsId}/cursors/?canvas_id=${activeCanvasId}`,
                { credentials: 'include', headers }
            );
            
            if (!response.ok) {
                console.warn('[AssistantOverlay] Failed to load cursors:', response.status);
                safeSetInnerHTML(selectEl, '<option value="">Failed to load cursors</option>');
                return;
            }
            
            const cursors = await response.json();
            
            // Clear existing options
            safeSetInnerHTML(selectEl, '');
            
            // Add "My Cursor" option (uses the 'me' endpoint)
            const myOption = document.createElement('option');
            myOption.value = 'me';
            myOption.textContent = 'My Cursor (default)';
            myOption.selected = true;
            selectEl.appendChild(myOption);
            
            // Add separator and canvas-specific cursors
            if (cursors.length > 0) {
                const separator = document.createElement('option');
                separator.disabled = true;
                separator.textContent = '── Canvas Cursors ──';
                selectEl.appendChild(separator);
                
                cursors.forEach(cursor => {
                    const option = document.createElement('option');
                    option.value = cursor.id;
                    // Show cursor name or truncated ID
                    const displayName = cursor.name || cursor.id;
                    option.textContent = displayName.length > 25 
                        ? `${displayName.substring(0, 25)}...` 
                        : displayName;
                    selectEl.appendChild(option);
                });
            }
            
            selectEl.disabled = false;
            
            // Restore from localStorage if available
            const savedCursorId = localStorage.getItem('assistant_cursor_id');
            if (savedCursorId && savedCursorId !== 'me') {
                // Check if saved cursor exists in options
                const exists = cursors.some(c => c.id === savedCursorId);
                if (exists) {
                    selectEl.value = savedCursorId;
                    // Don't auto-execute LOAD CURSOR on restore
                }
            }
            
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load cursors:', err);
            safeSetInnerHTML(selectEl, '<option value="">Error loading cursors</option>');
        }
    }
    
    /**
     * Handle cursor selection change
     * When a cursor other than 'me' is selected, execute RESET + LOAD CURSOR on 'me'
     */
    async _onCursorChange(cursorId, save = true) {
        const previousCursorId = this.selectedCursorId;
        this.selectedCursorId = cursorId || null;
        
        if (save && cursorId) {
            localStorage.setItem('assistant_cursor_id', cursorId);
        } else if (save) {
            localStorage.removeItem('assistant_cursor_id');
        }
        
        // If selecting a specific cursor (not 'me'), load it into the 'me' cursor
        if (cursorId && cursorId !== 'me' && cursorId !== previousCursorId) {
            await this._loadCursorIntoMe(cursorId);
        }
        
        // Load cursor stats
        await this._loadCursorStats();
        
        // Propagate to A2UIChat if active
        if (this.a2uiChat && typeof this.a2uiChat.setCursor === 'function') {
            this.a2uiChat.setCursor(cursorId);
        }
        
        console.log('[AssistantOverlay] Cursor changed:', cursorId);
    }
    
    /**
     * Load a cursor into 'me' by executing RESET + LOAD CURSOR on the 'me' endpoint
     * This allows the user to work with a specific cursor's state via the 'me' interface
     */
    async _loadCursorIntoMe(cursorId) {
        const wsId = this.selectedWorkspaceId || this.options.workspace_id;
        if (!this._hasApiEndpoint() || !wsId || !cursorId) return;
        
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            // Execute RESET followed by LOAD CURSOR on the 'me' cursor
            const query = `RESET; LOAD CURSOR('${cursorId}');`;
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${wsId}/cursors/me/execute`,
                { 
                    method: 'POST',
                    credentials: 'include', 
                    headers,
                    body: JSON.stringify({ query })
                }
            );
            
            if (response.ok) {
                console.log('[AssistantOverlay] Loaded cursor into me:', cursorId);
                // Refresh graph explorer if active
                if (this.graphExplorer && this.activeTab === 'explorer') {
                    await this._loadGraphDataIntoExplorer();
                }
            } else {
                const error = await response.json().catch(() => ({}));
                console.warn('[AssistantOverlay] Failed to load cursor:', error.detail || response.status);
            }
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load cursor into me:', err);
        }
    }
    
    /**
     * Load cursor stats from the 'me' cursor data endpoint
     * Displays node/edge counts in the cursor stats section
     */
    async _loadCursorStats() {
        const nodesEl = this.container.querySelector('#cursor-stat-nodes');
        const edgesEl = this.container.querySelector('#cursor-stat-edges');
        
        if (!nodesEl || !edgesEl) return;
        
        const wsId = this.selectedWorkspaceId || this.options.workspace_id;
        if (!this._hasApiEndpoint() || !wsId) {
            nodesEl.textContent = '-';
            edgesEl.textContent = '-';
            return;
        }
        
        try {
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            // Call /workspaces/{id}/cursors/me/data to get cursor data
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${wsId}/cursors/me/data`,
                { credentials: 'include', headers }
            );
            
            if (!response.ok) {
                console.warn('[AssistantOverlay] Failed to load cursor stats:', response.status);
                nodesEl.textContent = '-';
                edgesEl.textContent = '-';
                return;
            }
            
            const data = await response.json();
            
            // Count nodes and edges from the response
            // Expected format: { nodes: [...], edges: [...] } or similar
            const nodeCount = Array.isArray(data.nodes) ? data.nodes.length : 0;
            const edgeCount = Array.isArray(data.edges) ? data.edges.length : 0;
            
            nodesEl.textContent = nodeCount;
            edgesEl.textContent = edgeCount;
            
            console.log('[AssistantOverlay] Cursor stats loaded:', { nodes: nodeCount, edges: edgeCount });
            
        } catch (err) {
            console.warn('[AssistantOverlay] Failed to load cursor stats:', err);
            nodesEl.textContent = '-';
            edgesEl.textContent = '-';
        }
    }
    
    /**
     * Select a persona
     */
    _selectPersona(personaId, personaName) {
        this.selectedPersonaId = personaId;
        
        // Update header button
        const nameEl = this.container.querySelector('#assistant-persona-name');
        if (nameEl) nameEl.textContent = personaName;
        
        // Update dropdown selection
        const items = this.container.querySelectorAll('.persona-dropdown-item');
        items.forEach(item => {
            item.classList.toggle('selected', item.dataset.personaId === personaId);
        });
        
        // Close dropdown
        const selector = this.container.querySelector('.assistant-persona-selector');
        if (selector) selector.classList.remove('open');
        
        // Propagate to A2UIChat if active (pass both ID and name)
        if (this.a2uiChat && typeof this.a2uiChat.setPersona === 'function') {
            this.a2uiChat.setPersona(personaId, personaName);
        }
        
        console.log('[AssistantOverlay] Selected persona:', personaId, personaName);
    }

    /**
     * Toggle the overlay
     */
    toggle(open = null) {
        if (!this.container) return;
        
        this.isOpen = open !== null ? open : !this.isOpen;
        this.container.classList.toggle('open', this.isOpen);

        if (this.isOpen) {
            this._loadCurrentTab();
            this._updatePageContext();
        }
    }

    /**
     * Set active tab
     */
    setActiveTab(tabId) {
        if (!this.container) return;
        
        this.activeTab = tabId;

        // Update tab buttons
        const tabs = this.container.querySelectorAll('.assistant-tab[data-tab]');
        tabs.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabId);
        });

        // Update tab content
        const contents = this.container.querySelectorAll('.assistant-tab-content');
        contents.forEach(content => {
            content.classList.toggle('active', content.dataset.tabContent === tabId);
        });

        this._loadCurrentTab();

        // Re-init icons after tab switch
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }
    }

    _loadCurrentTab() {
        console.log('[AssistantOverlay] Loading tab:', this.activeTab);
        if (this.activeTab === 'chat') {
            if (!this.chatPanel) {
                this._loadChatPanel();
            } else if (this.a2uiChat) {
                // Chat already loaded - sync workspace if it changed
                const currentWorkspace = this.selectedWorkspaceId || this.options.workspace_id;
                if (currentWorkspace && this.a2uiChat.options.workspaceId !== currentWorkspace) {
                    console.log('[AssistantOverlay] Syncing workspace to chat:', currentWorkspace);
                    this.a2uiChat.setWorkspace(currentWorkspace, this.options.api_endpoint, this.options.access_token);
                }
            }
        } else if (this.activeTab === 'explorer') {
            if (!this.graphExplorer) {
                console.log('[AssistantOverlay] Loading explorer tab...');
                this._loadGraphExplorer();
            } else {
                // Explorer already loaded - sync workspace if it changed
                const currentWorkspace = this.selectedWorkspaceId || this.options.workspace_id;
                if (currentWorkspace && this.graphExplorer.options.workspace_id !== currentWorkspace) {
                    console.log('[AssistantOverlay] Syncing workspace to explorer:', currentWorkspace);
                    this.graphExplorer.setWorkspace(currentWorkspace);
                }
            }
        } else if (this.activeTab === 'artefacts' && !this.artefactsPanel) {
            this._loadArtefactsPanel();
        }
    }

    async _checkAuthentication() {
        try {
            // 1. Check for global token variable (both underscore styles)
            const globalToken = (typeof window !== 'undefined' && (window.FAXIOM_API_TOKEN || window.FAXIOM__API_TOKEN));
            if (globalToken) {
                return globalToken;
            }
            
            // 2. Check Dash's 'user' store from sessionStorage (Dash app context)
            const userStoreData = sessionStorage.getItem('user');
            if (userStoreData) {
                const user = JSON.parse(userStoreData);
                if (user && user.token) {
                    return user.token;
                }
            }
            
            // 3. Fallback: Check extension storage (browser extension context)
            const data = await storage.get(['user.token', 'user']);
            return data['user.token'] || data.user?.token || null;
        } catch (error) {
            return null;
        }
    }

    async _loadChatPanel() {
        const container = this.container.querySelector('#assistant-chat-container');
        if (!container) return;

        // Use A2UIChat from @merlin/chat (imported at top level)
        try {
            // A2UIChat is now imported at module level from @merlin/chat
            
            // Clear loading state
            safeSetInnerHTML(container, '<div id="a2ui-chat-wrapper" style="height: 100%;"></div>');
            const wrapper = container.querySelector('#a2ui-chat-wrapper');
            
            // Get token for API calls
            const token = this.options.access_token || await this._checkAuthentication();
            
            // Use selectedWorkspaceId if available (user selected from dropdown), fallback to options
            const workspaceId = this.selectedWorkspaceId || this.options.workspace_id;
            
            console.log('[AssistantOverlay] Creating A2UIChat with:', {
                apiEndpoint: this.options.api_endpoint,
                workspaceId: workspaceId,
                hasToken: !!token
            });
            
            // Create A2UIChat instance
            this.a2uiChat = new A2UIChat({
                apiEndpoint: this.options.api_endpoint,
                workspaceId: workspaceId,
                canvasId: this.selectedCanvasId || this.canvasId,
                accessToken: token,
                layoutMode: this.isExpanded ? 'full' : 'compact',
                theme: this.options.theme,
                onError: (err) => {
                    console.error('[AssistantOverlay] Chat error:', err);
                },
            });
            
            await this.a2uiChat.init(wrapper);
            
            // Pass current persona if already selected
            if (this.selectedPersonaId) {
                const personaName = this.container.querySelector('#assistant-persona-name')?.textContent || 'You';
                this.a2uiChat.setPersona(this.selectedPersonaId, personaName);
            }
            
            // Pass current canvas and cursor if selected in Context tab
            if (this.selectedCanvasId) {
                this.a2uiChat.setCanvas(this.selectedCanvasId);
            }
            if (this.selectedCursorId) {
                this.a2uiChat.setCursor(this.selectedCursorId);
            }
            
            console.log('[AssistantOverlay] A2UIChat initialized');
            
            // Mark as loaded
            this.chatPanel = true;
            
        } catch (err) {
            console.error('[AssistantOverlay] Failed to load A2UIChat:', err);
            captureError(err, {
                tags: { operation: 'initChatPanel' },
                extra: {
                    workspace_id: workspaceId,
                    canvas_id: this.selectedCanvasId || this.canvasId,
                }
            });
            
            // Fallback to simple chat UI
            this._loadFallbackChatPanel();
        }
    }

    /**
     * Fallback chat panel if A2UIChat fails to load
     */
    async _loadFallbackChatPanel() {
        const container = this.container.querySelector('#assistant-chat-container');
        if (!container) return;

        const token = await this._checkAuthentication();
        const showSelectors = !this.options.workspace_id && token;

        // Create integrated chat UI
        safeSetInnerHTML(container, `
            <div class="integrated-chat">
                ${showSelectors ? `
                <div class="chat-selectors" style="padding: 12px; border-bottom: 1px solid var(--border-color); display: flex; flex-direction: column; gap: 8px;">
                    <div style="font-size: 11px; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em;">Workspace Context</div>
                    <div style="display: flex; gap: 8px;">
                        <select id="chat-workspace-select" style="flex: 1; padding: 6px; border-radius: 4px; border: 1px solid var(--border-color); background: var(--bg-secondary); color: var(--text-primary); font-size: 13px;">
                            <option value="">Loading workspaces...</option>
                        </select>
                        <select id="chat-stream-select" style="flex: 1; padding: 6px; border-radius: 4px; border: 1px solid var(--border-color); background: var(--bg-secondary); color: var(--text-primary); font-size: 13px;" disabled>
                            <option value="">Select Stream...</option>
                        </select>
                    </div>
                </div>
                ` : ''}
                <div class="integrated-chat-messages" id="chat-messages">
                    <div class="empty-state">
                        <div class="empty-state-icon">
                            <i data-lucide="message-circle"></i>
                        </div>
                        <h3>Start a Conversation</h3>
                        <p>Ask questions about your workspace or search for entities</p>
                    </div>
                </div>
                <div class="integrated-chat-input">
                    <div class="chat-search-container">
                        <div class="chat-suggestions" id="chat-suggestions" style="display:none;"></div>
                        <div class="chat-input-wrapper">
                            <input type="text" id="chat-input" placeholder="Search entities or ask a question..." autocomplete="off">
                            <button class="chat-send-btn" id="chat-send-btn">
                                <i data-lucide="send"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `);

        if (showSelectors) {
            this._loadWorkspaceSelectors(token);
        }

        // Initialize icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }

        // Setup chat event handlers
        this._setupChatHandlers();

        // Mark as loaded
        this.chatPanel = true;
    }

    async _loadWorkspaceSelectors(token) {
        const wsSelect = this.container.querySelector('#chat-workspace-select');
        const streamSelect = this.container.querySelector('#chat-stream-select');
        if (!wsSelect || !this._hasApiEndpoint()) return;

        try {
            const response = await fetch(`${this.options.api_endpoint}/workspaces/`, {
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to load workspaces');

            const data = await response.json();
            const workspaces = data.workspaces || data.results || data || [];

            safeSetInnerHTML(wsSelect, '<option value="">Select Workspace...</option>' +
                workspaces.map(ws => `<option value="${ws.id}">${ws.name || ws.id}</option>`).join(''));

            wsSelect.addEventListener('change', async (e) => {
                const wsId = e.target.value;
                if (!wsId) {
                    streamSelect.disabled = true;
                    return;
                }

                // Update active workspace
                this.options.workspace_id = wsId;
                // Remove warning if it exists
                const warning = this.container.querySelector('.workspace-warning');
                if (warning) warning.remove();

                // Load streams
                streamSelect.disabled = false;
                safeSetInnerHTML(streamSelect, '<option value="">Loading streams...</option>');

                try {
                    const sResponse = await fetch(`${this.options.api_endpoint}/workspaces/${wsId}/streams/`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                        credentials: 'include'
                    });
                    if (!sResponse.ok) throw new Error('Failed to load streams');
                    const sData = await sResponse.json();
                    const streams = sData.streams || sData.results || sData || [];

                    safeSetInnerHTML(streamSelect, '<option value="">Select Stream...</option>' +
                        streams.map(s => `<option value="${s.id}">${s.name || s.id}</option>`).join(''));
                } catch (err) {
                    console.error('Error loading streams:', err);
                    safeSetInnerHTML(streamSelect, '<option value="">Error loading streams</option>');
                }
            });
        } catch (error) {
            console.error('Error loading workspaces:', error);
            safeSetInnerHTML(wsSelect, '<option value="">Error loading</option>');
        }
    }

    _setupChatHandlers() {
        const input = this.container.querySelector('#chat-input');
        const sendBtn = this.container.querySelector('#chat-send-btn');
        const suggestions = this.container.querySelector('#chat-suggestions');

        if (!input || !sendBtn) return;

        // Search debounce
        let searchTimeout;

        // Input handler with search suggestions
        input.addEventListener('input', (e) => {
            const query = e.target.value.trim();

            clearTimeout(searchTimeout);

            if (query.length >= 2 && this.options.api_endpoint && this.options.workspace_id) {
                searchTimeout = setTimeout(() => this._searchEntities(query), 300);
            } else {
                if (suggestions) suggestions.style.display = 'none';
            }
        });

        // Enter to send
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this._sendChatMessage();
            }
            if (e.key === 'Escape') {
                if (suggestions) suggestions.style.display = 'none';
            }
        });

        // Send button
        sendBtn.addEventListener('click', () => this._sendChatMessage());
    }

    async _searchEntities(query) {
        const suggestions = this.container.querySelector('#chat-suggestions');
        if (!suggestions || !this._hasApiEndpoint() || !this.options.workspace_id) return;

        try {
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/search/suggest?query=${encodeURIComponent(query)}&limit=5`,
                { credentials: 'include' }
            );

            if (!response.ok) throw new Error('Search failed');

            const results = await response.json();

            if (results.length === 0) {
                suggestions.style.display = 'none';
                return;
            }

            safeSetInnerHTML(suggestions, results.map(item => `
                <div class="suggestion-item" data-id="${item.id}" data-type="${item.type}">
                    <div class="suggestion-icon">
                        <i data-lucide="${this._getEntityIcon(item.type)}"></i>
                    </div>
                    <div class="suggestion-content">
                        <div class="suggestion-title">${this._escapeHtml(item.label || item.name)}</div>
                        <div class="suggestion-type">${item.type}</div>
                    </div>
                </div>
            `).join(''));

            suggestions.style.display = 'block';

            // Initialize icons
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }

            // Click handlers for suggestions
            suggestions.querySelectorAll('.suggestion-item').forEach(item => {
                item.addEventListener('click', () => {
                    const input = this.container.querySelector('#chat-input');
                    if (input) {
                        input.value = item.querySelector('.suggestion-title').textContent;
                    }
                    suggestions.style.display = 'none';

                    // Optionally load in explorer
                    if (this.graphExplorer) {
                        this._loadEntityInGraph(item.dataset.id, item.dataset.type);
                    }
                });
            });

        } catch (error) {
            console.error('Search error:', error);
            suggestions.style.display = 'none';
        }
    }

    _getEntityIcon(type) {
        const icons = {
            'Person': 'user',
            'Organization': 'building-2',
            'Location': 'map-pin',
            'Document': 'file-text',
            'Concept': 'lightbulb',
            'Event': 'calendar',
            'Product': 'package',
            'default': 'circle-dot'
        };
        return icons[type] || icons.default;
    }

    async _sendChatMessage() {
        const input = this.container.querySelector('#chat-input');
        const messagesContainer = this.container.querySelector('#chat-messages');
        const suggestions = this.container.querySelector('#chat-suggestions');

        if (!input || !messagesContainer) return;

        const content = input.value.trim();
        if (!content) return;

        // Hide suggestions
        if (suggestions) suggestions.style.display = 'none';

        // Clear empty state
        const emptyState = messagesContainer.querySelector('.empty-state');
        if (emptyState) emptyState.remove();

        // Add user message
        const userMessageHtml = `
            <div class="integrated-chat-message user">
                <div class="message-bubble">${this._escapeHtml(content)}</div>
            </div>
        `;
        safeInsertAdjacentHTML(messagesContainer, 'beforeend', userMessageHtml);

        // Clear input
        input.value = '';

        // Scroll to bottom
        messagesContainer.scrollTop = messagesContainer.scrollHeight;

        // Send to API if configured - use A2A protocol with SSE streaming
        if (this.options.api_endpoint && this.options.workspace_id) {
            await this._sendA2AMessage(content, messagesContainer);
        }
    }

    /**
     * Send message using A2A protocol with SSE streaming
     */
    async _sendA2AMessage(content, messagesContainer) {
        // Add streaming indicator
        const streamingIndicatorId = `streaming-${Date.now()}`;
        const streamingHtml = `
            <div class="integrated-chat-message assistant" id="${streamingIndicatorId}">
                <div class="message-bubble">
                    <div class="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        `;
        safeInsertAdjacentHTML(messagesContainer, 'beforeend', streamingHtml);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        
        try {
            const requestBody = {
                jsonrpc: '2.0',
                id: crypto.randomUUID(),
                method: 'message/stream',
                params: {
                    message: {
                        role: 'user',
                        parts: [{ kind: 'text', text: content }],
                    },
                    configuration: {
                        ...(this.canvasId && { canvasId: this.canvasId }),
                        ...(this._currentTaskId && { taskId: this._currentTaskId }),
                    },
                },
            };

            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization if access_token is available
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }

            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/a2a`,
                {
                    method: 'POST',
                    headers,
                    credentials: 'include',
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Handle SSE stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let agentResponse = '';
            let agentName = 'Assistant';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') continue;

                        try {
                            const event = JSON.parse(data);
                            const result = event.result;

                            if (result) {
                                // Capture task ID for continuing conversations
                                if (result.taskId && !this._currentTaskId) {
                                    this._currentTaskId = result.taskId;
                                }

                                // Extract message content
                                if (result.kind === 'message' && result.status?.message) {
                                    const msg = result.status.message;
                                    const parts = msg.parts || [];
                                    agentResponse = parts
                                        .filter(p => p.kind === 'text')
                                        .map(p => p.text)
                                        .join('\n');
                                    
                                    // Get agent name if available
                                    if (msg.agent_id) {
                                        agentName = msg.agent_id;
                                    }
                                }
                            }
                        } catch (parseErr) {
                            console.warn('[A2A] Parse error:', parseErr);
                        }
                    }
                }
            }

            // Remove streaming indicator
            const streamingEl = document.getElementById(streamingIndicatorId);
            if (streamingEl) streamingEl.remove();

            // Add agent response
            if (agentResponse) {
                const assistantMessageHtml = `
                    <div class="integrated-chat-message assistant">
                        <div class="message-bubble">
                            <div class="message-header">
                                <span>${this._escapeHtml(agentName)}</span>
                            </div>
                            ${this._escapeHtml(agentResponse)}
                        </div>
                    </div>
                `;
                safeInsertAdjacentHTML(messagesContainer, 'beforeend', assistantMessageHtml);
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }

        } catch (error) {
            console.error('[A2A] Stream error:', error);
            captureError(error, {
                tags: { operation: 'a2aStream' },
                extra: {
                    workspace_id: this.options.workspace_id,
                    canvas_id: this.canvasId,
                }
            });
            
            // Remove streaming indicator
            const streamingEl = document.getElementById(streamingIndicatorId);
            if (streamingEl) streamingEl.remove();

            const errorHtml = `
                <div class="integrated-chat-message assistant">
                    <div class="message-bubble error">
                        <em>Unable to get response: ${this._escapeHtml(error.message)}</em>
                    </div>
                </div>
            `;
            safeInsertAdjacentHTML(messagesContainer, 'beforeend', errorHtml);
        }
    }

    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    async _loadGraphExplorer() {
        const container = this.container.querySelector('#assistant-graph-container');
        if (!container) return;

        console.log('[AssistantOverlay] Loading full GraphExplorer...');

        // Resolve API endpoint and workspace
        const apiEndpoint = await this._resolveApiEndpoint();
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        const auth = this._getSessionAuth();

        console.log('[AssistantOverlay] Graph config:', { apiEndpoint, workspaceId, hasAuth: !!auth });

        // Store resolved config for later use
        this._graphConfig = { apiEndpoint, workspaceId, auth };

        // Create the explorer container - full height, no extra padding
        safeSetInnerHTML(container, `
            <div id="graph-explorer-root" class="graph-explorer-embedded"></div>
        `);

        const explorerContainer = container.querySelector('#graph-explorer-root');
        const isDark = this.options.theme === 'dark';

        // Initialize MerlinGraph - full explorer with UI modules (toolbar, tabs, windows, panels)
        // Show MerlinGraph's own tabs (Graph, Bookmarks, Timeline, Analytics, Artefacts) within the Explorer panel
        this.graphExplorer = new MerlinGraph(explorerContainer, {
            // Theme & Display - full UI mode to match standalone graph extension
            theme: isDark ? 'dark' : 'light',
            show_toolbar: true,      // Left toolbar for graph actions
            show_tabs: true,         // ENABLED: Show MerlinGraph's tabs (Graph, Bookmarks, Timeline, Analytics, Artefacts)
            show_legend: true,       // Legend window
            show_fql: false,         // FQL editor (optional)
            show_assistant: false,   // Don't show nested assistant (we ARE the assistant)
            
            // Physics & Layout
            physics_enabled: true,
            physics_solver: 'forceAtlas2Based',
            
            // API Configuration
            api_endpoint: apiEndpoint,
            workspace_id: workspaceId,
            cursor_id: 'me',  // Always use "me" - API resolves to user's default cursor
            
            // Pass auth from AssistantOverlay session
            auth_token: auth?.token || null,
            api_key: auth?.apiKey || null,
            
            // Not in standalone browser extension mode
            extension_mode: false,
            
            // Callbacks for API operations
            onExecuteQuery: async (query) => {
                return this._executeGraphQuery(query);
            },
            onOperator: async (op, params) => {
                return this._handleGraphOperator(op, params);
            },
            onSelectionChange: (selection) => {
                console.log('[AssistantOverlay] Graph selection changed:', selection);
                // Could update selection info in sidebar
            },
            onReload: async () => {
                return this._fetchGraphData();
            }
        });

        // Wait for explorer to be ready
        await this.graphExplorer.ready();

        console.log('[AssistantOverlay] MerlinGraph loaded and ready');

        // Load initial data if we have workspace configured
        // api_endpoint can be empty string (for proxy mode)
        if (apiEndpoint !== null && apiEndpoint !== undefined && workspaceId) {
            await this._loadGraphDataIntoExplorer();
        } else {
            console.log('[AssistantOverlay] No API endpoint or workspace configured - using empty state');
        }

        // Setup compact tabs detection based on container width
        this._setupCompactTabsDetection(explorerContainer);

        // When Explorer is active, we can optionally disable Chat/Artefacts tabs
        // since GraphExplorer has its own chat features
        this._updateTabVisibilityForExplorer(true);
    }

    /**
     * Setup ResizeObserver to detect when the graph container is narrow
     * and apply compact-tabs class to show icon-only tabs
     */
    _setupCompactTabsDetection(container) {
        const graphExplorer = container.querySelector('.graph-explorer');
        if (!graphExplorer) return;

        // Threshold for compact mode (same as graph extension's "side" layout)
        const COMPACT_THRESHOLD = 500;

        const updateCompactMode = () => {
            const width = container.offsetWidth;
            if (width < COMPACT_THRESHOLD) {
                graphExplorer.classList.add('compact-tabs');
            } else {
                graphExplorer.classList.remove('compact-tabs');
            }
        };

        // Initial check
        updateCompactMode();

        // Watch for resize
        if (typeof ResizeObserver !== 'undefined') {
            this._graphResizeObserver = new ResizeObserver(updateCompactMode);
            this._graphResizeObserver.observe(container);
        }
    }

    /**
     * Update tab visibility when Explorer is active
     * GraphExplorer has its own integrated features, so we may hide duplicate tabs
     */
    _updateTabVisibilityForExplorer(explorerActive) {
        // Option to hide Chat/Artefacts when Explorer is loaded since it has its own
        // For now, we keep them visible but could toggle with:
        // const chatTab = this.container.querySelector('.assistant-tab[data-tab="chat"]');
        // const artefactsTab = this.container.querySelector('.assistant-tab[data-tab="artefacts"]');
        // if (chatTab) chatTab.style.display = explorerActive ? 'none' : '';
        // if (artefactsTab) artefactsTab.style.display = explorerActive ? 'none' : '';
        
        // For now, just log the state
        console.log('[AssistantOverlay] Explorer active:', explorerActive);
    }

    /**
     * Load GraphExplorer CSS styles dynamically
     */
    async _loadGraphExplorerStyles() {
        if (document.getElementById('graph-explorer-styles')) return;

        // Try to load from extension resources first
        const cssUrls = [
            getResourceURL('graph/styles/graph-explorer.css'),
            '/assets/graph-explorer.css',
            'https://cdn.jsdelivr.net/npm/@merlin/graph/dist/graph-explorer.css'
        ];

        for (const url of cssUrls) {
            try {
                const link = document.createElement('link');
                link.id = 'graph-explorer-styles';
                link.rel = 'stylesheet';
                link.href = url;
                document.head.appendChild(link);
                console.log('[AssistantOverlay] Loaded graph explorer styles from:', url);
                return;
            } catch (e) {
                console.warn('[AssistantOverlay] Failed to load styles from:', url);
            }
        }

        // Inject minimal inline styles as fallback
        const style = document.createElement('style');
        style.id = 'graph-explorer-styles';
        style.textContent = `
            .graph-explorer {
                position: relative;
                width: 100%;
                height: 100%;
                min-height: 400px;
                background: var(--ma-bg-primary, #1a1a2e);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                overflow: hidden;
            }
            .graph-explorer .graph-canvas {
                position: absolute;
                inset: 0;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Load graph data into the GraphExplorer
     */
    async _loadGraphDataIntoExplorer() {
        if (!this.graphExplorer) return;

        const apiEndpoint = await this._resolveApiEndpoint();
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        const auth = this._getSessionAuth();

        if (!apiEndpoint || !workspaceId) {
            console.warn('[AssistantOverlay] Cannot load graph data - missing API endpoint or workspace');
            return;
        }

        try {
            // Build headers
            const headers = { 'Content-Type': 'application/json' };
            if (auth?.method === 'bearer' && auth.token) {
                headers['Authorization'] = `Bearer ${auth.token}`;
            } else if (auth?.method === 'apikey' && auth.apiKey) {
                headers['X-API-Key'] = auth.apiKey;
            }

            // Always use "me" cursor - API resolves to user's default cursor
            const cursorId = 'me';

            // Reset cursor first to clear state/history
            try {
                const resetUrl = `${apiEndpoint}/workspaces/${workspaceId}/cursors/${cursorId}/reset`;
                await fetch(resetUrl, { method: 'POST', headers, credentials: 'include' });
                console.log('[AssistantOverlay] Cursor reset');
            } catch (e) {
                console.warn('[AssistantOverlay] Cursor reset failed (may not exist yet):', e);
            }

            // Fetch from cursor endpoint
            const url = `${apiEndpoint}/workspaces/${workspaceId}/cursors/${cursorId}/graph/data`;
            const response = await fetch(url, { headers, credentials: 'include' });
            
            let nodes = [];
            let edges = [];
            
            if (response.ok) {
                const data = await response.json();
                nodes = data.nodes || [];
                edges = data.edges || [];
            }

            // Set data in explorer - GraphExplorer.setData expects (nodes, edges) as separate args
            if (this.graphExplorer.setData) {
                this.graphExplorer.setData(nodes, edges);
            } else if (this.graphExplorer.explorer?.setData) {
                this.graphExplorer.explorer.setData(nodes, edges);
            }

            console.log(`[AssistantOverlay] Loaded ${nodes.length} nodes, ${edges.length} edges into GraphExplorer`);

        } catch (error) {
            console.error('[AssistantOverlay] Failed to load graph data:', error);
            captureError(error, {
                tags: { operation: 'loadGraphData' },
                extra: {
                    workspace_id: this.options.workspace_id,
                    cursor_id: this.cursorId,
                }
            });
        }
    }

    /**
     * Execute a graph query (MQL)
     */
    async _executeGraphQuery(query) {
        const apiEndpoint = await this._resolveApiEndpoint();
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        const auth = this._getSessionAuth();

        if (!apiEndpoint || !workspaceId) {
            return { error: 'No API endpoint or workspace configured' };
        }

        const headers = { 'Content-Type': 'application/json' };
        if (auth?.method === 'bearer' && auth.token) {
            headers['Authorization'] = `Bearer ${auth.token}`;
        } else if (auth?.method === 'apikey' && auth.apiKey) {
            headers['X-API-Key'] = auth.apiKey;
        }

        try {
            const url = this.cursorId 
                ? `${apiEndpoint}/workspaces/${workspaceId}/cursors/${this.cursorId}/execute`
                : `${apiEndpoint}/workspaces/${workspaceId}/execute`;

            const response = await fetch(url, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({ query })
            });

            if (!response.ok) {
                throw new Error(`Query failed: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('[AssistantOverlay] Query execution failed:', error);
            captureError(error, {
                tags: { operation: 'executeQuery' },
                extra: {
                    workspace_id: workspaceId,
                    cursor_id: this.cursorId,
                    query: query.substring(0, 200),
                }
            });
            return { error: error.message };
        }
    }

    /**
     * Handle graph operators (extend, connect, etc.)
     */
    async _handleGraphOperator(op, params) {
        console.log('[AssistantOverlay] Graph operator:', op, params);
        // Operators are handled by the GraphExplorer internally
        // This callback is for custom handling if needed
        return { success: true };
    }

    /**
     * Fetch graph data for refresh operations
     */
    async _fetchGraphData() {
        await this._loadGraphDataIntoExplorer();
    }

    /**
     * Fallback simple graph explorer (original vis.js implementation)
     */
    async _loadSimpleGraphExplorer(container, apiEndpoint, workspaceId, auth) {
        console.log('[AssistantOverlay] Loading simple graph explorer...');

        // Create integrated graph UI
        safeSetInnerHTML(container, `
            <div class="integrated-graph">
                <div class="graph-toolbar">
                    <button class="graph-toolbar-btn" data-action="fit" title="Fit to view">
                        <i data-lucide="maximize-2"></i>
                    </button>
                    <button class="graph-toolbar-btn" data-action="physics" title="Toggle physics">
                        <i data-lucide="atom"></i>
                    </button>
                    <div class="graph-toolbar-divider"></div>
                    <button class="graph-toolbar-btn" data-action="refresh" title="Refresh">
                        <i data-lucide="refresh-cw"></i>
                    </button>
                </div>
                <div class="graph-canvas" id="graph-canvas">
                    <div id="mynetwork" style="width: 100%; height: 100%; min-height: 400px;"></div>
                    <div class="graph-status">
                        <div class="graph-status-dot"></div>
                        <span id="graph-status-text">Ready</span>
                    </div>
                </div>
            </div>
        `);

        // Initialize icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }

        // Store resolved config for use in other methods
        this._graphConfig = { apiEndpoint, workspaceId, auth };

        // Load vis.js and initialize network
        await this._initVisNetwork();

        // Setup toolbar handlers
        this._setupGraphHandlers();

        // Load initial data if workspace_id is set
        if (workspaceId && apiEndpoint) {
            this._loadWorkspaceGraph();
        }

        // Mark as loaded (true = simple mode)
        this.graphExplorer = true;
    }

    async _initVisNetwork() {
        console.log('[AssistantOverlay] Initializing vis.js network...');
        
        // Load vis.js from CDN if not available
        if (typeof vis === 'undefined') {
            console.log('[AssistantOverlay] Loading vis.js from CDN...');
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/vis-network@9.1.9/dist/vis-network.min.js';
                script.onload = () => {
                    console.log('[AssistantOverlay] vis.js loaded successfully');
                    resolve();
                };
                script.onerror = (err) => {
                    console.error('[AssistantOverlay] Failed to load vis.js:', err);
                    reject(err);
                };
                document.head.appendChild(script);
            });
        }

        const networkContainer = this.container.querySelector('#mynetwork');
        if (!networkContainer) {
            console.error('[AssistantOverlay] Network container #mynetwork not found');
            return;
        }
        if (typeof vis === 'undefined') {
            console.error('[AssistantOverlay] vis.js not available');
            return;
        }

        console.log('[AssistantOverlay] Creating vis.js DataSets...');
        
        // Initialize data
        this.graphNodes = new vis.DataSet([]);
        this.graphEdges = new vis.DataSet([]);

        const isDark = this.options.theme === 'dark';

        // Network options
        const networkOptions = {
            nodes: {
                shape: 'dot',
                size: 16,
                font: {
                    size: 12,
                    color: isDark ? '#f8fafc' : '#1e293b',
                },
                borderWidth: 2,
                shadow: true,
                color: {
                    border: isDark ? '#3B82F6' : '#2563EB',
                    background: isDark ? '#1e293b' : '#ffffff',
                    highlight: {
                        border: '#3B82F6',
                        background: isDark ? '#334155' : '#f1f5f9',
                    },
                },
            },
            edges: {
                width: 1,
                color: {
                    color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    highlight: '#3B82F6',
                },
                smooth: {
                    type: 'continuous',
                },
                arrows: {
                    to: { enabled: true, scaleFactor: 0.5 },
                },
            },
            physics: {
                enabled: true,
                stabilization: {
                    iterations: 100,
                    fit: true,
                },
                barnesHut: {
                    gravitationalConstant: -2000,
                    springConstant: 0.04,
                    damping: 0.09,
                },
            },
            interaction: {
                hover: true,
                tooltipDelay: 200,
                hideEdgesOnDrag: true,
            },
        };

        // Create network
        console.log('[AssistantOverlay] Creating vis.js Network instance...');
        this.network = new vis.Network(
            networkContainer,
            { nodes: this.graphNodes, edges: this.graphEdges },
            networkOptions
        );
        console.log('[AssistantOverlay] vis.js Network created successfully');

        // Fit view after stabilization
        this.network.on('stabilizationIterationsDone', () => {
            console.log('[AssistantOverlay] Stabilization done, fitting view');
            this.network.fit({ animation: true });
        });

        // Selection handlers
        this.network.on('selectNode', (params) => {
            this._updateGraphStatus(`Selected: ${params.nodes.length} node(s)`);
        });

        this.network.on('deselectNode', () => {
            this._updateGraphStatus('Ready');
        });
    }

    _setupGraphHandlers() {
        const toolbar = this.container.querySelector('.graph-toolbar');
        if (!toolbar) return;

        toolbar.addEventListener('click', (e) => {
            const btn = e.target.closest('.graph-toolbar-btn');
            if (!btn) return;

            const action = btn.dataset.action;
            switch (action) {
                case 'fit':
                    this.network?.fit({ animation: true });
                    break;
                case 'physics':
                    const physicsEnabled = !this.network?.physics?.options?.enabled;
                    this.network?.setOptions({ physics: { enabled: physicsEnabled } });
                    btn.classList.toggle('active', physicsEnabled);
                    break;
                case 'refresh':
                    this._loadWorkspaceGraph();
                    break;
            }
        });
    }

    async _loadWorkspaceGraph() {
        console.log('[AssistantOverlay] Loading workspace graph...');
        
        // Use resolved config from graph explorer or resolve now
        const apiEndpoint = this._graphConfig?.apiEndpoint || await this._resolveApiEndpoint() || this.options.api_endpoint;
        const workspaceId = this._graphConfig?.workspaceId || this._getSelectedWorkspaceId() || this.options.workspace_id;
        const auth = this._graphConfig?.auth || this._getSessionAuth();
        
        console.log('[AssistantOverlay] api_endpoint:', apiEndpoint);
        console.log('[AssistantOverlay] workspace_id:', workspaceId);
        
        if (!apiEndpoint || !workspaceId) {
            console.warn('[AssistantOverlay] No workspace configured');
            this._updateGraphStatus('No workspace configured');
            return;
        }

        this._updateGraphStatus('Loading...');
        
        try {
            // Build auth headers
            const headers = {};
            if (auth?.method === 'bearer' && auth.token) {
                headers['Authorization'] = `Bearer ${auth.token}`;
            } else if (auth?.method === 'apikey' && auth.apiKey) {
                headers['X-API-Key'] = auth.apiKey;
            } else if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            // Use the cursor's graph data endpoint if we have a cursor
            let url;
            let nodes = [];
            let edges = [];
            
            if (this.cursorId) {
                url = `${apiEndpoint}/workspaces/${workspaceId}/cursors/${this.cursorId}/graph/data`;
                console.log('[AssistantOverlay] Fetching graph data from:', url);
                
                const response = await fetch(url, { 
                    credentials: 'include',
                    headers 
                });
    
                if (!response.ok) throw new Error('Failed to load graph');
    
                const data = await response.json();
                console.log('[AssistantOverlay] Graph data received:', data);
                
                nodes = data.nodes || [];
                edges = data.edges || [];
            } else {
                // Fetch nodes and edges separately when no cursor
                const nodesUrl = `${apiEndpoint}/workspaces/${workspaceId}/nodes/?limit=50`;
                const edgesUrl = `${apiEndpoint}/workspaces/${workspaceId}/edges/?limit=100`;
                
                console.log('[AssistantOverlay] Fetching nodes from:', nodesUrl);
                console.log('[AssistantOverlay] Fetching edges from:', edgesUrl);
                
                const [nodesResponse, edgesResponse] = await Promise.all([
                    fetch(nodesUrl, { credentials: 'include', headers }),
                    fetch(edgesUrl, { credentials: 'include', headers })
                ]);
    
                if (!nodesResponse.ok) throw new Error('Failed to load nodes');
    
                const nodesData = await nodesResponse.json();
                nodes = Array.isArray(nodesData) ? nodesData : (nodesData.nodes || []);
                
                // Edges might fail if endpoint doesn't exist, that's ok
                if (edgesResponse.ok) {
                    const edgesData = await edgesResponse.json();
                    edges = Array.isArray(edgesData) ? edgesData : (edgesData.edges || []);
                }
                
                console.log('[AssistantOverlay] Nodes received:', nodes.length);
                console.log('[AssistantOverlay] Edges received:', edges.length);
            }

            // Clear and add data
            this.graphNodes.clear();
            this.graphEdges.clear();
            
            console.log('[AssistantOverlay] Processing nodes:', nodes.length, 'edges:', edges.length);

            if (nodes.length > 0) {
                const mappedNodes = nodes.map(n => ({
                    id: n.id,
                    label: n.label || n.name || n.internal_id || 'Unknown',
                    color: this._getNodeColor(n.type || n.mixin_name),
                    title: `${n.type || n.mixin_name || 'Node'}: ${n.label || n.name || n.internal_id}`,
                }));
                console.log('[AssistantOverlay] Adding nodes to graph:', mappedNodes.slice(0, 3));
                this.graphNodes.add(mappedNodes);
            }

            if (edges.length > 0) {
                this.graphEdges.add(edges.map(e => ({
                    from: e.source || e.from,
                    to: e.target || e.to,
                    label: e.label || e.type,
                })));
            }

            // Fit view
            setTimeout(() => {
                this.network?.fit({ animation: true });
            }, 100);

            this._updateGraphStatus(`${nodes.length} nodes, ${edges.length} edges`);

        } catch (error) {
            console.error('Graph load error:', error);
            this._updateGraphStatus('Error loading graph');
        }
    }

    _getNodeColor(type) {
        // Map various node types to colors
        const colors = {
            // Standard entity types
            'Person': '#3B82F6',
            'Organization': '#10B981',
            'Location': '#F59E0B',
            'Document': '#8B5CF6',
            'Concept': '#EC4899',
            'Event': '#06B6D4',
            // Common mixin names from the nodes endpoint
            'person': '#3B82F6',
            'organization': '#10B981',
            'location': '#F59E0B',
            'document': '#8B5CF6',
            'concept': '#EC4899',
            'event': '#06B6D4',
            'fact': '#F97316',
            'axiom': '#EF4444',
            'taxonomy': '#14B8A6',
            'agent': '#8B5CF6',
        };
        return colors[type] || colors[type?.toLowerCase()] || '#64748B';
    }

    _updateGraphStatus(text) {
        const statusText = this.container.querySelector('#graph-status-text');
        if (statusText) statusText.textContent = text;
    }

    async _loadEntityInGraph(id, type) {
        if (!this.network || !this._hasApiEndpoint() || !this.options.workspace_id) return;

        try {
            // Use the nodes endpoint to get the entity and its relationships
            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }
            
            const response = await fetch(
                `${this.options.api_endpoint}/workspaces/${this.options.workspace_id}/nodes/${id}`,
                { 
                    credentials: 'include',
                    headers 
                }
            );

            if (!response.ok) throw new Error('Failed to load entity');

            const data = await response.json();

            // Add to existing graph
            if (data.nodes) {
                const existingIds = this.graphNodes.getIds();
                const newNodes = data.nodes.filter(n => !existingIds.includes(n.id));
                this.graphNodes.add(newNodes.map(n => ({
                    id: n.id,
                    label: n.label || n.name,
                    color: this._getNodeColor(n.type),
                })));
            }

            if (data.edges) {
                this.graphEdges.add(data.edges.map(e => ({
                    from: e.source || e.from,
                    to: e.target || e.to,
                    label: e.label || e.type,
                })));
            }

            // Focus on the entity
            this.network.focus(id, { animation: true, scale: 1.5 });

        } catch (error) {
            console.error('Entity load error:', error);
        }
    }

    // ========================================
    // ARTEFACTS PANEL
    // ========================================

    async _loadArtefactsPanel() {
        const container = this.container.querySelector('#assistant-artefacts-container');
        if (!container) return;

        // Create artefacts UI
        safeSetInnerHTML(container, `
            <div class="artefacts-panel">
                <div class="artefacts-section">
                    <div class="artefacts-section-header">
                        <i data-lucide="file-box"></i>
                        <h4>Artefacts</h4>
                        <button class="artefacts-refresh-btn" id="refresh-artefacts" title="Refresh">
                            <i data-lucide="refresh-cw"></i>
                        </button>
                    </div>
                    <div class="artefacts-list" id="artefacts-list">
                        <div class="artefacts-empty-state">
                            <i data-lucide="file-box"></i>
                            <p>No artefacts yet</p>
                        </div>
                    </div>
                </div>
            </div>
        `);

        // Initialize icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }

        // Setup event handlers
        this._setupArtefactsHandlers();

        // Load initial data
        await this._loadArtefacts();

        // Mark as loaded
        this.artefactsPanel = true;
    }

    _setupArtefactsHandlers() {
        const refreshBtn = this.container.querySelector('#refresh-artefacts');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this._loadArtefacts());
        }
    }

    /**
     * Get the appropriate icon for an artefact type
     */
    _getArtefactIcon(type) {
        const icons = {
            'text': 'file-text',
            'audio': 'music',
            'video': 'video',
            'image': 'image',
            'facts': 'database',
            'default': 'file',
        };
        return icons[type?.toLowerCase()] || icons.default;
    }

    /**
     * Get the CSS class for an artefact type badge
     */
    _getArtefactTypeClass(type) {
        const classes = {
            'text': 'type-info',
            'audio': 'type-purple',
            'video': 'type-warning',
            'image': 'type-success',
            'facts': 'type-primary',
        };
        return classes[type?.toLowerCase()] || '';
    }

    /**
     * Format a file size in bytes to a human-readable string
     */
    _formatFileSize(bytes) {
        if (!bytes) return '';
        const num = parseInt(bytes, 10);
        if (isNaN(num)) return '';
        if (num < 1024) return `${num} B`;
        if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
        return `${(num / (1024 * 1024)).toFixed(1)} MB`;
    }

    _formatDate(dateString) {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                if (diffHours === 0) {
                    const diffMins = Math.floor(diffMs / (1000 * 60));
                    return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
                }
                return `${diffHours}h ago`;
            } else if (diffDays === 1) {
                return 'Yesterday';
            } else if (diffDays < 7) {
                return `${diffDays}d ago`;
            } else {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
        } catch {
            return '';
        }
    }

    async _loadArtefacts() {
        const list = this.container.querySelector('#artefacts-list');
        if (!list || !this._hasApiEndpoint() || !this.options.workspace_id) return;

        safeSetInnerHTML(list, '<div class="artefacts-loading-inline"><div class="spinner-small"></div></div>');

        try {
            const workspaceId = this.selectedWorkspaceId || this.options.workspace_id;
            let url = `${this.options.api_endpoint}/api/workspaces/${workspaceId}/artefacts/?limit=50`;
            
            // Filter by canvas if one is selected
            const canvasId = this.selectedCanvasId || this.canvasId;
            if (canvasId) {
                url += `&canvas_id=${canvasId}`;
            }

            const headers = {};
            if (this.options.access_token) {
                headers['Authorization'] = `Bearer ${this.options.access_token}`;
            }

            const response = await fetch(url, {
                credentials: 'include',
                headers,
            });

            if (!response.ok) throw new Error('Failed to load artefacts');

            const data = await response.json();
            // API returns a list directly
            this.artefacts = Array.isArray(data) ? data : (data.artefacts || data.results || []);

            if (this.artefacts.length === 0) {
                safeSetInnerHTML(list, `
                    <div class="artefacts-empty-state">
                        <i data-lucide="file-box"></i>
                        <p>No artefacts yet</p>
                        <span class="artefacts-empty-hint">Artefacts created during tasks will appear here</span>
                    </div>
                `);
            } else {
                safeSetInnerHTML(list, this.artefacts.map(artefact => {
                    const artType = artefact.data?.id || artefact.mixin_name || 'file';
                    const artName = artefact.data?.name || artefact.name || 'Untitled';
                    const artSize = artefact.data?.bytes ? this._formatFileSize(artefact.data.bytes.length) : '';

                    return `
                        <div class="artefact-card" data-id="${artefact.id}" data-type="${artType}">
                            <div class="artefact-card-icon ${this._getArtefactTypeClass(artType)}">
                                <i data-lucide="${this._getArtefactIcon(artType)}"></i>
                            </div>
                            <div class="artefact-card-content">
                                <div class="artefact-card-title">${this._escapeHtml(artName)}</div>
                                <div class="artefact-card-meta">
                                    <span class="artefact-card-type">${artType}</span>
                                    ${artSize ? `<span class="artefact-card-size">${artSize}</span>` : ''}
                                    <span class="artefact-card-date">${this._formatDate(artefact.created_at)}</span>
                                </div>
                            </div>
                            <button class="artefact-card-download" title="Download artefact" data-id="${artefact.id}">
                                <i data-lucide="download"></i>
                            </button>
                        </div>
                    `;
                }).join(''));

                // Setup download handlers
                list.querySelectorAll('.artefact-card-download').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        this._downloadArtefact(btn.dataset.id);
                    });
                });
            }

            // Re-init icons
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }

        } catch (error) {
            console.error('Artefacts load error:', error);
            safeSetInnerHTML(list, `
                <div class="artefacts-empty-state error">
                    <i data-lucide="alert-circle"></i>
                    <p>Failed to load artefacts</p>
                </div>
            `);
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }
    }

    /**
     * Download an artefact to disk
     */
    async _downloadArtefact(artefactId) {
        const artefact = this.artefacts.find(a => a.id === artefactId);
        if (!artefact) return;

        const artType = artefact.data?.id || 'file';
        const artName = artefact.data?.name || artefact.name || 'artefact';

        try {
            if (artType === 'text') {
                // Text/markdown content — download as .md or .txt
                const content = artefact.data?.content || '';
                const ext = artName.includes('.') ? '' : '.md';
                const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
                this._triggerDownload(blob, `${artName}${ext}`);
            } else if (['audio', 'video', 'image'].includes(artType)) {
                // Binary content stored as base64 in bytes field
                const base64 = artefact.data?.bytes || '';
                const mimeTypes = {
                    'audio': 'audio/mpeg',
                    'video': 'video/mp4',
                    'image': 'image/png',
                };
                const extensions = {
                    'audio': '.mp3',
                    'video': '.mp4',
                    'image': '.png',
                };
                const mime = mimeTypes[artType] || 'application/octet-stream';
                const ext = artName.includes('.') ? '' : (extensions[artType] || '');

                // Decode base64 to binary
                const binaryStr = atob(base64);
                const bytes = new Uint8Array(binaryStr.length);
                for (let i = 0; i < binaryStr.length; i++) {
                    bytes[i] = binaryStr.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: mime });
                this._triggerDownload(blob, `${artName}${ext}`);
            } else {
                // Fallback: fetch from API and download
                const workspaceId = this.selectedWorkspaceId || this.options.workspace_id;
                const headers = {};
                if (this.options.access_token) {
                    headers['Authorization'] = `Bearer ${this.options.access_token}`;
                }
                const response = await fetch(
                    `${this.options.api_endpoint}/api/workspaces/${workspaceId}/artefacts/${artefactId}`,
                    { credentials: 'include', headers }
                );
                if (!response.ok) throw new Error('Failed to fetch artefact');
                const data = await response.json();
                const content = JSON.stringify(data, null, 2);
                const blob = new Blob([content], { type: 'application/json' });
                const ext = artName.includes('.') ? '' : '.json';
                this._triggerDownload(blob, `${artName}${ext}`);
            }
        } catch (error) {
            console.error('Artefact download error:', error);
        }
    }

    /**
     * Trigger browser file download
     */
    _triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    _updatePageContext() {
        const titleEl = this.container.querySelector('#ctx-page-title');
        const urlEl = this.container.querySelector('#ctx-page-url');

        if (titleEl) titleEl.textContent = document.title || 'Untitled';
        if (urlEl) urlEl.textContent = window.location.href || '-';

        // Re-init icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }
    }

    async _extractEntities() {
        const btn = this.container.querySelector('#ctx-extract-btn');
        const entitiesContainer = this.container.querySelector('#ctx-entities');

        // Get workspace ID from context selector or options
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        
        if (!entitiesContainer || !this._hasApiEndpoint() || !workspaceId) {
            console.warn('[AssistantOverlay] Cannot extract: missing endpoint or workspace');
            return;
        }

        // Show loading state
        if (btn) {
            btn.disabled = true;
            safeSetInnerHTML(btn, '<i data-lucide="loader-2" class="animate-spin"></i> Extracting...');
        }

        try {
            // Get page content
            const pageContent = document.body.innerText.substring(0, 10000);

            // Use the search/analyze endpoint
            const response = await fetch(`${this.options.api_endpoint}/workspaces/${workspaceId}/search/analyze`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(this.options.access_token && { 'Authorization': `Bearer ${this.options.access_token}` })
                },
                credentials: 'include',
                body: JSON.stringify({
                    text: pageContent,
                    include_nodes: true,
                    include_taxonomy_resources: true,
                    include_taxonomy_types: true,
                    include_ontology_types: true,
                    min_token_length: 2,
                    max_ngram_size: 3,
                    case_sensitive: false,
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            const matches = data.matches || [];

            if (matches.length === 0) {
                safeSetInnerHTML(entitiesContainer, `
                    <div class="context-empty-state">
                        <i data-lucide="search-x"></i>
                        <p>No entities found on this page</p>
                    </div>
                `);
                this.pageContext.entities = [];
            } else {
                // Convert matches to entity format and render
                const entities = matches.map(m => ({
                    id: m.id,
                    name: m.name,
                    type: m.entity_type,
                    mixin_name: m.mixin_name,
                    matched_text: m.matched_text,
                    positions: m.positions,
                    confidence: m.confidence,
                }));
                
                safeSetInnerHTML(entitiesContainer, `
                    <div class="context-entity-list">
                        ${entities.map(e => `
                            <span class="context-entity-chip" data-id="${e.id}" data-type="${e.type}" title="${this._escapeHtml(e.matched_text)}">
                                <i data-lucide="${this._getEntityIcon(e.type)}"></i>
                                ${this._escapeHtml(e.name)}
                            </span>
                        `).join('')}
                    </div>
                `);

                this.pageContext.entities = entities;
                
                // Highlight entities on the page
                this._highlightEntitiesOnPage(entities);
            }

        } catch (error) {
            console.error('Extract error:', error);
            safeSetInnerHTML(entitiesContainer, `
                <div class="context-empty-state">
                    <i data-lucide="alert-circle"></i>
                    <p>Failed to extract entities: ${error.message}</p>
                </div>
            `);
        } finally {
            // Reset button
            if (btn) {
                btn.disabled = false;
                safeSetInnerHTML(btn, '<i data-lucide="scan"></i> Extract Entities');
            }

            // Re-init icons
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }
    }
    
    /**
     * Highlight extracted entities on the page
     */
    _highlightEntitiesOnPage(entities) {
        // Remove existing highlights
        this._removeHighlights();
        
        if (!entities || entities.length === 0) return;
        
        // Inject highlight styles if not present
        if (!document.getElementById('merlin-highlight-styles')) {
            const style = document.createElement('style');
            style.id = 'merlin-highlight-styles';
            style.textContent = `
                .merlin-entity-highlight {
                    background: rgba(59, 130, 246, 0.2);
                    border-bottom: 2px solid #3b82f6;
                    padding: 2px 0;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .merlin-entity-highlight:hover {
                    background: rgba(59, 130, 246, 0.35);
                }
                .merlin-entity-highlight[data-type="node"] {
                    background: rgba(16, 185, 129, 0.2);
                    border-bottom-color: #10b981;
                }
                .merlin-entity-highlight[data-type="taxonomy_resource"],
                .merlin-entity-highlight[data-type="concept"] {
                    background: rgba(139, 92, 246, 0.2);
                    border-bottom-color: #8b5cf6;
                }
                .merlin-entity-highlight[data-type="ontology_type"],
                .merlin-entity-highlight[data-type="edge_type"] {
                    background: rgba(245, 158, 11, 0.2);
                    border-bottom-color: #f59e0b;
                }
            `;
            document.head.appendChild(style);
        }
        
        // Collect all unique matched texts
        const matchedTexts = [...new Set(entities.map(e => e.matched_text || e.name))];
        
        // Walk through text nodes and highlight matches
        const walker = document.createTreeWalker(
            document.body,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    // Skip script, style, and our overlay
                    const parent = node.parentElement;
                    if (!parent) return NodeFilter.FILTER_REJECT;
                    const tagName = parent.tagName.toLowerCase();
                    if (['script', 'style', 'noscript', 'textarea', 'input'].includes(tagName)) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    if (parent.closest('#merlin-assistant-overlay')) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    if (parent.classList.contains('merlin-entity-highlight')) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );
        
        const nodesToProcess = [];
        let node;
        while (node = walker.nextNode()) {
            const text = node.textContent;
            for (const matchText of matchedTexts) {
                if (text.toLowerCase().includes(matchText.toLowerCase())) {
                    const entity = entities.find(e => (e.matched_text || e.name).toLowerCase() === matchText.toLowerCase());
                    nodesToProcess.push({ node, matchText, entity });
                    break;
                }
            }
        }
        
        // Process nodes (in reverse to avoid position shifts)
        for (const { node, matchText, entity } of nodesToProcess.reverse()) {
            this._highlightTextInNode(node, matchText, entity);
        }
    }
    
    /**
     * Highlight specific text in a text node
     */
    _highlightTextInNode(textNode, searchText, entity) {
        const text = textNode.textContent;
        const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        if (parts.length <= 1) return;
        
        const fragment = document.createDocumentFragment();
        
        for (const part of parts) {
            if (part.toLowerCase() === searchText.toLowerCase()) {
                const span = document.createElement('span');
                span.className = 'merlin-entity-highlight';
                span.setAttribute('data-type', entity?.type || 'unknown');
                span.setAttribute('data-entity-id', entity?.id || '');
                span.textContent = part;
                fragment.appendChild(span);
            } else if (part) {
                fragment.appendChild(document.createTextNode(part));
            }
        }
        
        textNode.parentNode.replaceChild(fragment, textNode);
    }
    
    /**
     * Remove all entity highlights from the page
     */
    _removeHighlights() {
        document.querySelectorAll('.merlin-entity-highlight').forEach(el => {
            const text = el.textContent;
            el.replaceWith(document.createTextNode(text));
        });
    }
    
    /**
     * Get selected workspace ID from context tab selector
     */
    _getSelectedWorkspaceId() {
        const selector = this.container.querySelector('#ctx-workspace-select, #chat-workspace-select');
        return selector?.value || this.options.workspace_id;
    }
    
    /**
     * Show graph selection modal
     */
    async _showGraphModal() {
        if (!this.pageContext.entities?.length) {
            alert('No entities to add. Extract entities first.');
            return;
        }
        
        const modal = this.container.querySelector('#graph-selection-modal');
        const select = this.container.querySelector('#stream-select');
        
        if (!modal || !select) return;
        
        // Show modal
        modal.style.display = 'flex';
        
        // Load streams
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        if (!workspaceId || !this._hasApiEndpoint()) {
            safeSetInnerHTML(select, '<option value="">No workspace selected</option>');
            return;
        }
        
        safeSetInnerHTML(select, '<option value="">Loading streams...</option>');
        
        try {
            const response = await fetch(`${this.options.api_endpoint}/workspaces/${workspaceId}/streams/`, {
                headers: {
                    ...(this.options.access_token && { 'Authorization': `Bearer ${this.options.access_token}` })
                },
                credentials: 'include',
            });
            
            if (!response.ok) throw new Error('Failed to load streams');
            
            const streams = await response.json();
            
            if (streams.length === 0) {
                safeSetInnerHTML(select, '<option value="">No streams available</option>');
            } else {
                safeSetInnerHTML(select, `
                    <option value="">Select a stream...</option>
                    ${streams.map(s => `<option value="${s.id}">${this._escapeHtml(s.name)}</option>`).join('')}
                `);
            }
        } catch (error) {
            console.error('Failed to load streams:', error);
            safeSetInnerHTML(select, '<option value="">Failed to load streams</option>');
        }
        
        // Re-init icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons({ attrs: { class: 'lucide-icon' } });
        }
    }
    
    /**
     * Hide graph selection modal
     */
    _hideGraphModal() {
        const modal = this.container.querySelector('#graph-selection-modal');
        if (modal) {
            modal.style.display = 'none';
        }
    }
    
    /**
     * Add extracted entities to graph via document creation
     */
    async _addToGraph() {
        const select = this.container.querySelector('#stream-select');
        const streamId = select?.value;
        
        if (!streamId) {
            alert('Please select a stream.');
            return;
        }
        
        const confirmBtn = this.container.querySelector('#modal-confirm-btn');
        if (confirmBtn) {
            confirmBtn.disabled = true;
            safeSetInnerHTML(confirmBtn, '<i data-lucide="loader-2" class="animate-spin"></i> Adding...');
        }
        
        const workspaceId = this._getSelectedWorkspaceId() || this.options.workspace_id;
        const pageUrl = window.location.href;
        const pageTitle = document.title || 'Extracted Page';
        
        try {
            // Create a webpage document using the correct schema
            const response = await fetch(`${this.options.api_endpoint}/workspaces/${workspaceId}/documents/`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(this.options.access_token && { 'Authorization': `Bearer ${this.options.access_token}` })
                },
                credentials: 'include',
                body: JSON.stringify({
                    name: pageTitle,
                    data: {
                        id: 'webpage',
                        url: pageUrl,
                        name: pageTitle,
                        notes: JSON.stringify({
                            entities: this.pageContext.entities,
                            extracted_at: new Date().toISOString(),
                        }),
                        stream_id: streamId,
                    },
                })
            });

            if (response.ok) {
                this._hideGraphModal();
                alert('Content added to graph successfully!');
            } else {
                const error = await response.json();
                throw new Error(error.detail || JSON.stringify(error) || 'Failed to add to graph');
            }
        } catch (error) {
            console.error('Add to graph error:', error);
            alert(`Failed to add to graph: ${error.message}`);
        } finally {
            if (confirmBtn) {
                confirmBtn.disabled = false;
                safeSetInnerHTML(confirmBtn, 'Add to Graph');
            }
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }
    }

    async _addToCursor() {
        if (!this.pageContext.entities?.length || !this._hasApiEndpoint()) {
            alert('No entities to add. Extract entities first.');
            return;
        }

        const btn = this.container.querySelector('#ctx-add-cursor-btn');
        if (btn) {
            btn.disabled = true;
            safeSetInnerHTML(btn, '<i data-lucide="loader-2" class="animate-spin"></i> Adding...');
        }

        try {
            const response = await fetch(`${this.options.api_endpoint}/api/cursor/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    entities: this.pageContext.entities,
                    workspace_id: this.options.workspace_id,
                    source_url: window.location.href,
                })
            });

            if (response.ok) {
                alert('Entities added to workspace!');
            } else {
                throw new Error('Failed to add');
            }
        } catch (error) {
            console.error('Add to cursor error:', error);
            alert('Failed to add entities. Please try again.');
        } finally {
            if (btn) {
                btn.disabled = false;
                safeSetInnerHTML(btn, '<i data-lucide="plus"></i> Add to Workspace');
            }
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }
    }

    /**
     * Set page context
     */
    setPageContext(context) {
        this.pageContext = context;

        const titleEl = this.container.querySelector('#ctx-page-title');
        const urlEl = this.container.querySelector('#ctx-page-url');
        const entitiesEl = this.container.querySelector('#ctx-entities');

        if (titleEl && context.title) titleEl.textContent = context.title;
        if (urlEl && context.url) urlEl.textContent = context.url;

        if (entitiesEl && context.entities?.length) {
            safeSetInnerHTML(entitiesEl, `
                <div class="context-entity-list">
                    ${context.entities.map(e => `
                        <span class="context-entity-chip">
                            ${this._escapeHtml(e.label || e.name)}
                        </span>
                    `).join('')}
                </div>
            `);
        }
    }

    /**
     * Set cursor ID for explorer tab
     */
    setCursor(cursorId) {
        this.cursorId = cursorId;
    }

    /**
     * Set canvas ID for task scoping
     */
    setCanvasId(canvasId) {
        this.canvasId = canvasId;
        this.options.canvas_id = canvasId;
    }

    /**
     * Set workspace ID for API calls
     */
    setWorkspaceId(workspaceId) {
        this.options.workspace_id = workspaceId;
    }

    /**
     * Set access token for API authentication
     */
    setAccessToken(accessToken) {
        this.options.access_token = accessToken;
    }

    /**
     * Set workspace ID and reload relevant panels
     */
    setWorkspace(workspaceId) {
        this.options.workspace_id = workspaceId;
        this.selectedWorkspaceId = workspaceId;
        console.log('[AssistantOverlay] Workspace changed to:', workspaceId);
        
        // Update A2UIChat if it exists
        if (this.a2uiChat) {
            // Destroy and recreate to pick up new workspace
            this.a2uiChat.destroy();
            this.a2uiChat = null;
        }
        
        // Reset chat panel to reload with new workspace
        this.chatPanel = null;
        this._currentTaskId = null;
        
        // Reload current tab if it's chat
        if (this.activeTab === 'chat') {
            this._loadChatPanel();
        }
        
        // Reload explorer if it was loaded
        if (this.graphExplorer) {
            this.graphExplorer = null;
            if (this.activeTab === 'explorer') {
                this._loadGraphExplorer();
            }
        }
    }

    /**
     * Toggle expanded/compact mode
     */
    toggleExpanded() {
        this.isExpanded = !this.isExpanded;
        
        // Toggle expanded class on container for CSS targeting
        if (this.isExpanded) {
            this.container.classList.add('expanded');
        } else {
            this.container.classList.remove('expanded');
        }
        
        const panel = this.container.querySelector('.assistant-panel');
        if (panel) {
            if (this.isExpanded) {
                panel.style.width = '80vw';
                panel.style.minWidth = 'auto';
                panel.style.maxWidth = 'calc(100vw - 32px)';
            } else {
                panel.style.width = this.options.width;
                panel.style.minWidth = this.options.min_width || '440px';
                panel.style.maxWidth = 'calc(100vw - 32px)';
            }
        }
        
        // Update A2UIChat layout mode if it exists
        if (this.a2uiChat) {
            this.a2uiChat.setLayoutMode(this.isExpanded ? 'full' : 'compact');
        }
        
        // Update icon
        const expandBtn = this.container.querySelector('#assistant-expand-btn i');
        if (expandBtn) {
            expandBtn.setAttribute('data-lucide', this.isExpanded ? 'minimize-2' : 'maximize-2');
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }
    }

    /**
     * Refresh the current tab
     */
    refresh() {
        // Reset the current tab's panel
        if (this.activeTab === 'chat') {
            // Destroy A2UIChat if it exists
            if (this.a2uiChat) {
                this.a2uiChat.destroy();
                this.a2uiChat = null;
            }
            this.chatPanel = null;
            this._loadChatPanel();
        } else if (this.activeTab === 'explorer') {
            this.graphExplorer = null;
            this._loadGraphExplorer();
        } else if (this.activeTab === 'artefacts') {
            this.artefactsPanel = null;
            this._loadArtefactsPanel();
        } else if (this.activeTab === 'context') {
            this._updatePageContext();
        }
    }

    /**
     * Set theme
     */
    setTheme(theme) {
        this.options.theme = theme;
        this._updateTheme();
    }

    _updateTheme() {
        if (!this.container) return;
        
        this.container.setAttribute('data-theme', this.options.theme);

        // Update theme icon
        const themeBtn = this.container.querySelector('#assistant-theme-btn i');
        if (themeBtn) {
            themeBtn.setAttribute('data-lucide', this.options.theme === 'dark' ? 'sun' : 'moon');
            if (typeof lucide !== 'undefined') {
                lucide.createIcons({ attrs: { class: 'lucide-icon' } });
            }
        }

        // Update A2UIChat theme if initialized
        if (this.a2uiChat && typeof this.a2uiChat.setTheme === 'function') {
            this.a2uiChat.setTheme(this.options.theme);
        }

        // Update GraphExplorer theme if initialized
        if (this.graphExplorer && typeof this.graphExplorer.setTheme === 'function') {
            this.graphExplorer.setTheme(this.options.theme);
        }

        // Update network colors if initialized
        if (this.network) {
            const isDark = this.options.theme === 'dark';
            this.network.setOptions({
                nodes: {
                    font: { color: isDark ? '#f8fafc' : '#1e293b' },
                    color: {
                        border: isDark ? '#3B82F6' : '#2563EB',
                        background: isDark ? '#1e293b' : '#ffffff',
                    },
                },
                edges: {
                    color: {
                        color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    },
                },
            });
        }
    }

    /**
     * Destroy the overlay
     */
    destroy() {
        // Clean up ResizeObserver
        if (this._graphResizeObserver) {
            this._graphResizeObserver.disconnect();
            this._graphResizeObserver = null;
        }
        
        if (this.network) {
            this.network.destroy();
            this.network = null;
        }
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
    }
}

export default AssistantOverlay;
